import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Upload, Download, Loader2, CheckCircle, AlertCircle, RotateCcw, Wand2, FolderOpen, ArrowRight,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { useSubscription } from '@/hooks/useSubscription';
import { useCredits, CREDIT_OPERATIONS } from '@/hooks/useCredits';
import { useCreditConfirm } from '@/components/credits/CreditConfirmDialog';
import { Slider } from '@/components/ui/slider';
import { UpgradeModal } from './subscription';
import { LoginModal } from './LoginModal';
import { ToolShell, StepCard, StepHeader, type ToolStep } from './tool-shell';
import LibraryPicker, { probeVideoUrl, stagedFileName, type StagedLibraryFile } from './library/LibraryPicker';
import { useNavigate } from 'react-router-dom';
import { SceneEditIntro } from './scene-edit/SceneEditIntro';
import { SceneEditVideoIntro } from './scene-edit/SceneEditVideoIntro';
import { SceneAnglesPanel } from './scene-edit/SceneAnglesPanel';
import { ShootCoachCard, ShotCheckCard, LightConflictNote, SavedSetsPicker, SaveSetForm, swapLeftRight, type ShotResult, type SceneSet } from './scene-edit/SceneShotAndSets';

// Scene Edit: Runway Aleph 2 rewrites a short stretch of the member's footage from a
// text instruction. Priced by length tier (backend: scene_edit_3/5/10/20/30).
type WizardStep = 'upload' | 'describe' | 'process' | 'download';

const STEPS: { key: WizardStep; label: string }[] = [
  { key: 'upload', label: 'Upload' },
  { key: 'describe', label: 'Describe' },
  { key: 'process', label: 'Process' },
  { key: 'download', label: 'Download' },
];
const STEP_ORDER: WizardStep[] = ['upload', 'describe', 'process', 'download'];

const TIERS = [
  { seconds: 3, credits: 2, op: CREDIT_OPERATIONS.SCENE_EDIT_3 },
  { seconds: 10, credits: 15, op: CREDIT_OPERATIONS.SCENE_EDIT_10 },
  { seconds: 20, credits: 20, op: CREDIT_OPERATIONS.SCENE_EDIT_20 },
  { seconds: 30, credits: 25, op: CREDIT_OPERATIONS.SCENE_EDIT_30 },
] as const;

// Describe the place and the light only. Asking Runway to change the people or
// what they hold (e.g. "give them a microphone") makes it redraw them, and they
// came back as cartoons in testing (Oct 9). Leaving people out kept them real.
// One-tap looks: prompts we've run through Aleph ourselves, with before/after stills
// from the same 5-second sample (frontend/public/scene-edit-looks/<key>.jpg).
// Snowy cabin was dropped Oct 10: the firelight relight changed the woman's face in
// two of three runs. Clean studio now says "empty ... no other people" (a bystander
// from the source footage showed up standing in the studio).
const LOOKS = [
  { key: 'golden-hour', label: 'Golden hour', prompt: 'Relight the scene with warm golden-hour sunlight and long soft shadows.' },
  { key: 'rainy-night', label: 'Rainy night', prompt: 'Change the setting to a city sidewalk on a rainy night with falling rain, glistening wet pavement and glowing streetlights.' },
  { key: 'night-city', label: 'Night city', prompt: 'Change the setting to a city street at night with neon signs and wet pavement reflections.' },
  { key: 'clean-studio', label: 'Clean studio', prompt: 'Replace the background with a clean, bright, empty white photo studio with soft even lighting and no other people.' },
  { key: 'moody-bar', label: 'Moody bar', prompt: 'Change the setting to a dim, moody cocktail bar with warm amber lamps.' },
] as const;

// Rooms (Oct 11 2026), from sferro21's "Any room, any angle" guide: name the lamps and
// windows and where they sit, put objects at different distances, and say where the
// light on the face comes from. The first two are his, trimmed to fit; the rest are
// ours, written the same way. The server wraps the room in his lock text (face, hair,
// the clothes by name, gestures, lip-sync, framing, timing unchanged).
const ROOMS = [
  { key: 'lounge', label: 'Lounge at night', room: 'a dark upscale lounge at night: warm terracotta plaster walls, a window wall of slatted blinds glowing cool cyan from the city behind, a brass arc floor lamp with a warm glass globe at camera-left, a white mushroom lamp on a walnut sideboard, a tall fiddle-leaf fig in the corner, an orange and blue abstract canvas against the wall; warm amber key from the arc lamp, cool cyan rim from the blinds, soft low fill' },
  { key: 'wheat-field', label: 'Wheat field', room: 'a wheat field under cloud, late afternoon, overcast and breezy: ripe faded-gold wheat running flat to the horizon and nodding in a steady breeze, a thin dark tree line and a small weathered farmhouse with a grain silo far behind, a wide sky of even grey-white cloud with the horizon low around the shoulders; soft overcast daylight from the whole sky, no hard shadows' },
  { key: 'study', label: 'Home study', room: 'a warm home study in the evening: walnut bookshelves full of books along the wall behind, a green-shaded banker\'s lamp on a desk at camera-left, a tall window at camera-right with blue dusk light through linen curtains, a leather armchair and a potted olive tree further back; warm key from the desk lamp, cool soft fill from the window' },
  { key: 'stage', label: 'Empty stage', room: 'an empty theater stage: one hard spotlight from above and slightly camera-left, a black velvet curtain several metres behind, a ghost light on a stand at the far right, a stack of road cases and a coiled rope at the edge of the light, rigging lines fading up into darkness; warm spotlight key on the face, deep shadow everywhere else' },
  { key: 'rooftop', label: 'Rooftop at dusk', room: 'a city rooftop at dusk: a low brick parapet a few metres behind, city towers with lit windows far in the distance, a string of warm bulb lights overhead running from camera-left, a potted palm and two metal chairs off to one side, the last orange glow on the horizon; warm bulb light as the key, cool blue sky light as the fill' },
  { key: 'loft', label: 'Sunlit loft', room: 'a sunlit loft apartment in the morning: tall steel-framed windows at camera-right throwing soft sunlight across the face, an exposed brick wall behind, a linen sofa and a floor lamp with a paper shade a few metres back, hanging plants and a wooden ladder shelf; warm window key, soft bounce fill from the pale floor' },
] as const;

// Likeness check on every render (server sceneLikeness.js): did the person keep
// their face? A drifted full render is refunded once, only when its preview passed.
type Likeness = { status: 'ok' | 'drift' | 'no_faces' | 'error'; score?: number | null; refunded?: boolean; refundNote?: string | null };
// Wording that asks the edit to change the person or what they hold: what makes
// faces drift (Oct 9: "give them a real microphone" turned a clip into cartoons).
// Mirrors DRIFT_WORDS in sceneLikeness.js.
const DRIFT_WORDS = /\b(give (him|her|them)|hand (him|her|them)|holding|hold(s)? an?\b|microphone|\bmic\b|props?\b|outfit|costume|clothes|clothing|wearing|dress(ed)? (him|her|them|as)|shirt|jacket|hat\b|glasses|hair(cut|style)?|makeup|beard|older|younger|age (him|her|them)|change (his|her|their) face|cartoon|anime|animated|pixar|claymation|turn (him|her|them) into|make (him|her|them) (look|into|a)|another (person|actor)|add (a )?(person|people|crowd|someone))\b/i;

// 5-second preview so people see the look before paying for a full edit.
const PREVIEW = { seconds: 5, credits: 3, op: CREDIT_OPERATIONS.SCENE_EDIT_5 } as const;

// Runway's own Aleph 2.0 guidance: start with an action verb, keep it short,
// describe the setting, one change at a time and build on the result.
const PROMPT_TIPS = [
  'Start with an action: Change, Replace, Relight, Remove.',
  'Keep it short and describe the place and the light.',
  'Make one change at a time. Like the result? Edit it again to add the next change.',
];

// Usage guide on the Describe step (GPT-written, Oct 10). The spend line under it is
// computed: the full edit plus 2 or 3 previews.
// Oct 22: the still (guide frame) is no longer a default step. It pastes the person onto
// the new place, which reads as green screen and then pins the render to that look; a
// description alone relit people better in testing. It stays available as an option.
const GUIDE_STEPS = [
  { title: 'Preview 5 seconds', cost: '3 video tokens', line: 'About 5 minutes. 2 or 3 tries is normal.' },
  { title: 'Render the full length', cost: '', line: 'Takes 10 to 15 minutes and saves to My Library.' },
];

// Swaps by line (Oct 22): a picked line is rendered padded a little each side so the cut
// lands between words. Runway needs at least 2 seconds.
const LINE_PAD = 0.2;
const LINE_MIN = 2;
const LINE_MAX = 30;
type SceneLine = { start: number; end: number; text: string };
const LINE_TIERS = [TIERS[0], { seconds: 5, credits: PREVIEW.credits, op: CREDIT_OPERATIONS.SCENE_EDIT_5 }, ...TIERS.slice(1)];
const lineTierFor = (len: number) => LINE_TIERS.find(t => t.seconds >= len - 0.001) || LINE_TIERS[LINE_TIERS.length - 1];

// Whole-scene range limits (server: LONG_SCENE_PIECE_SECONDS / LONG_SCENE_MAX_SECONDS)
const LONG_MIN = 30;
const LONG_MAX = 120;

// A job survives a reload: Aleph takes 10-15 minutes and people close tabs.
const PENDING_KEY = 'sceneEditPendingJob';

const fmt = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

interface VideoInfo {
  name: string;
  objectUrl: string;
  s3Key: string;
  s3Url: string;
  duration: number;
  // Shorter side in pixels, read from the file's metadata (0 until known)
  shortSide?: number;
  status: 'uploading' | 'converting' | 'uploaded' | 'error';
  uploadProgress?: number;
}

const pillButton = (active: boolean): React.CSSProperties => ({
  padding: 12,
  borderRadius: 'var(--hj-r-sm)',
  background: active ? 'var(--hj-blue-tint)' : 'var(--hj-surface-2)',
  border: `1px solid ${active ? 'var(--hj-blue)' : 'var(--hj-hairline-soft)'}`,
  color: 'var(--hj-ink)',
  cursor: 'pointer',
  fontFamily: 'inherit',
});

export const SceneEditTool = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { getAuthHeaders, getAuthToken } = useAuth();
  const { isSubscriber, loading: subscriptionLoading } = useSubscription();
  const { checkCredits } = useCredits();
  const { confirmCredit, CreditConfirmDialog } = useCreditConfirm();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showLibraryPicker, setShowLibraryPicker] = useState(false);

  const [currentStep, setCurrentStep] = useState<WizardStep>('upload');
  const [completedSteps, setCompletedSteps] = useState<Set<WizardStep>>(new Set());
  const [video, setVideo] = useState<VideoInfo | null>(null);

  const [tierSeconds, setTierSeconds] = useState<number>(10);
  // Swaps by line: the clip's lines, and the picked line (or run of lines) to render
  const [lines, setLines] = useState<{ status: 'idle' | 'loading' | 'ready' | 'failed'; items: SceneLine[]; error?: string }>({ status: 'idle', items: [] });
  const [lineSel, setLineSel] = useState<{ first: number; last: number; start: number; len: number } | null>(null);
  const [renderedLine, setRenderedLine] = useState<{ start: number; len: number } | null>(null);
  // Whole scene: 30s to 2 min, split into <=30s pieces server-side; needs a guide frame
  const [longMode, setLongMode] = useState(false);
  const [longEnd, setLongEnd] = useState(0);
  const [longQuote, setLongQuote] = useState<{ pieces: number; cost: number } | null>(null);
  const [longConfirm, setLongConfirm] = useState(false);
  const [longProgress, setLongProgress] = useState<{ done: number; total: number; stitching: boolean } | null>(null);
  const [start, setStart] = useState(0);
  const [prompt, setPrompt] = useState('');
  const [keepPeople, setKeepPeople] = useState(true);
  // 'room': a room plus his lock text, built server-side. 'look': the older one-line looks.
  const [promptMode, setPromptMode] = useState<'room' | 'look'>('room');
  const [wearing, setWearing] = useState('');
  const [wearingStatus, setWearingStatus] = useState<'idle' | 'reading' | 'done'>('idle');
  const wearingFor = useRef('');
  const roomMode = promptMode === 'room';
  // Each tab keeps what was typed in it, so flipping New room <-> Quick looks and back loses nothing.
  const parkedPrompt = useRef<Record<'room' | 'look', { prompt: string; improved: { from: string; note: string } | null; setId: string | null }>>({ room: { prompt: '', improved: null, setId: null }, look: { prompt: '', improved: null, setId: null } });
  // Prompt helper: rewrites a rough idea Runway-style; keeps the original for Undo
  const [improving, setImproving] = useState(false);
  // Guide frame: a still of this shot in the new setting (real people cut in), made from
  // the current start point. Sent to Runway as the first keyframe while it still matches.
  const [refPhotos, setRefPhotos] = useState<{ key: string; url: string }[]>([]);
  const [refUploading, setRefUploading] = useState(false);
  const [guide, setGuide] = useState<{ status: 'making' | 'ready' | 'failed'; key?: string; url?: string; start: number; error?: string; fromSet?: boolean } | null>(null);
  // Free shot check at the start point (face, angle, sharpness, exposure, light side)
  const [shot, setShot] = useState<{ checking: boolean; for: string; result: ShotResult | null }>({ checking: false, for: '', result: null });
  // Saved sets: reuse a setting across shots (server sceneSets.js)
  const [sets, setSets] = useState<SceneSet[]>([]);
  const [selectedSetId, setSelectedSetId] = useState<string | null>(null);
  const [savedGuideKeys, setSavedGuideKeys] = useState<string[]>([]);
  const [renderGuideKey, setRenderGuideKey] = useState<string | undefined>(undefined);
  const [useGuide, setUseGuide] = useState(true);
  // Oct 22: the still is opt-in. Open when asked, when a still or photo already exists, or for a whole scene (which needs one)
  const [showStill, setShowStill] = useState(false);
  // A guide frame belongs to one clip; a new source clip drops it
  // A new clip clears the guide, unless "Continue this scene" just set one up for it
  const guideBtnRef = useRef<HTMLButtonElement>(null);
  const pendingGuideRef = useRef<{ status: 'ready'; key: string; url: string; start: number } | null>(null);
  useEffect(() => {
    if (pendingGuideRef.current) { setGuide(pendingGuideRef.current); pendingGuideRef.current = null; }
    else setGuide(null);
    setLines({ status: 'idle', items: [] });
    setLineSel(null);
  }, [video?.s3Key]);
  const [improved, setImproved] = useState<{ from: string; note: string } | null>(null);
  // Preview: what was asked (so a changed prompt reads as stale) and its result
  const [preview, setPreview] = useState<{ status: 'running' | 'done' | 'failed'; url?: string; error?: string; startedAt: number; prompt: string; start: number; keepPeople: boolean; guideKey?: string; wearing?: string; likeness?: Likeness | null } | null>(null);
  const [finalLikeness, setFinalLikeness] = useState<Likeness | null>(null);
  const [previewElapsed, setPreviewElapsed] = useState(0);
  const previewTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [processStatus, setProcessStatus] = useState<'idle' | 'starting' | 'processing' | 'completed' | 'failed'>('idle');
  const [processError, setProcessError] = useState('');
  const [elapsed, setElapsed] = useState(0);
  const [finalVideoUrl, setFinalVideoUrl] = useState('');
  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previewRef = useRef<HTMLVideoElement>(null);
  const previewCardRef = useRef<HTMLDivElement>(null);

  const tier = lineSel ? lineTierFor(lineSel.len) : TIERS.find(t => t.seconds === tierSeconds)!;
  const duration = video?.duration || 0;
  const maxStart = Math.max(0, duration - tierSeconds);
  const end = longMode ? longEnd : lineSel ? lineSel.start + lineSel.len : duration > 0 ? Math.min(duration, start + tierSeconds) : start + tierSeconds;
  const canLong = duration > LONG_MIN + 0.5;
  const longLen = longEnd - start;
  // A range no longer than the preview renders straight away (Joe, Oct 30 2026): a
  // 5-second preview of a 2-second line costs more than the line itself.
  const shortRender = !longMode && end - start <= PREVIEW.seconds + 0.01;
  const shortLen = Math.round((end - start) * 10) / 10;

  // ===== NAVIGATION =====
  const stepIndex = STEP_ORDER.indexOf(currentStep);
  const markDoneAndGo = (from: WizardStep, to: WizardStep) => {
    setCompletedSteps(prev => new Set([...prev, from]));
    setCurrentStep(to);
  };

  const resetAll = () => {
    if (pollTimer.current) clearTimeout(pollTimer.current);
    try { localStorage.removeItem(PENDING_KEY); } catch { /* ignore */ }
    setVideo(null);
    setPrompt('');
    setStart(0);
    setProcessStatus('idle');
    setProcessError('');
    setFinalVideoUrl(''); setFinalLikeness(null);
    setCompletedSteps(new Set());
    setCurrentStep('upload');
    setSceneChain(null);
    setShot({ checking: false, for: '', result: null });
    setSelectedSetId(null);
  };

  const goToStep = (step: WizardStep) => {
    const targetIndex = STEP_ORDER.indexOf(step);
    if (targetIndex > stepIndex && !completedSteps.has(STEP_ORDER[targetIndex - 1])) return;
    if (currentStep === 'process' && (processStatus === 'processing' || processStatus === 'starting')) return;
    setCurrentStep(step);
  };

  // ===== STEP 1: UPLOAD =====
  const uploadFileToS3 = async (file: File) => {
    try {
      const res = await fetch('/get-upload-url', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ fileName: file.name, fileType: file.type || 'video/mp4', ephemeral: true }),
      });
      if (!res.ok) throw new Error('Failed to get upload URL');
      const { uploadUrl, key, downloadUrl } = await res.json();
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', file.type || 'video/mp4');
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            const pct = Math.round((e.loaded / e.total) * 100);
            setVideo(prev => prev ? { ...prev, uploadProgress: pct } : prev);
          }
        };
        xhr.onload = () => xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed: ${xhr.status}`));
        xhr.onerror = () => reject(new Error('Upload network error'));
        xhr.send(file);
      });
      return { key, downloadUrl };
    } catch (err: any) {
      setVideo(prev => prev ? { ...prev, status: 'error' as const } : prev);
      toast({ title: 'Upload failed', description: `${file.name}: ${err.message}`, variant: 'destructive' });
      return null;
    }
  };

  // Non-MP4 → MP4 via AWS MediaConvert (iPhone HDR tone-mapping), same as the other tools
  const convertClip = async (s3Key: string) => {
    try {
      const res = await fetch('/api/convert-mov', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ s3Key }),
      });
      if (!res.ok) throw new Error('Failed to start conversion');
      const { jobId } = await res.json();
      const poll = async () => {
        try {
          const r = await fetch(`/api/convert-mov/status/${jobId}`);
          const data = await r.json();
          if (data.status === 'completed') {
            setVideo(prev => prev ? {
              ...prev,
              s3Key: data.outputKey,
              s3Url: data.downloadUrl,
              objectUrl: `/api/s3/hey-joe-video-rotator/${data.outputKey}`,
              status: 'uploaded' as const,
            } : prev);
          } else if (data.status === 'failed') {
            setVideo(prev => prev ? { ...prev, status: 'error' as const } : prev);
            toast({ title: 'Conversion failed', description: data.error, variant: 'destructive' });
          } else {
            setTimeout(poll, 5000);
          }
        } catch (err: any) {
          setVideo(prev => prev ? { ...prev, status: 'error' as const } : prev);
          toast({ title: 'Conversion error', description: err.message, variant: 'destructive' });
        }
      };
      poll();
    } catch (err: any) {
      setVideo(prev => prev ? { ...prev, status: 'error' as const } : prev);
      toast({ title: 'Conversion failed', description: err.message, variant: 'destructive' });
    }
  };

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles.find(f =>
      f.type.startsWith('video/') || ['.mp4', '.mov', '.webm', '.mkv', '.avi'].some(ext => f.name.toLowerCase().endsWith(ext))
    );
    if (!file) {
      toast({ title: 'Invalid file', description: 'Please select a video file.', variant: 'destructive' });
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    setStart(0);
    setVideo({ name: file.name, objectUrl, s3Key: '', s3Url: '', duration: 0, status: 'uploading', uploadProgress: 0 });

    const temp = document.createElement('video');
    temp.preload = 'metadata';
    temp.src = objectUrl;
    temp.onloadedmetadata = () => setVideo(prev => prev ? { ...prev, duration: temp.duration, shortSide: Math.min(temp.videoWidth, temp.videoHeight) } : prev);

    const result = await uploadFileToS3(file);
    if (result) {
      if (!file.name.toLowerCase().endsWith('.mp4')) {
        setVideo(prev => prev ? { ...prev, s3Key: result.key, s3Url: result.downloadUrl, status: 'converting' as const } : prev);
        convertClip(result.key);
      } else {
        setVideo(prev => prev ? { ...prev, s3Key: result.key, s3Url: result.downloadUrl, status: 'uploaded' as const } : prev);
      }
    }
  }, [getAuthHeaders, toast]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'video/*': ['.mp4', '.mov', '.avi', '.mkv', '.webm'] },
    multiple: false,
  });

  const openLibraryPicker = () => {
    if (!getAuthToken()) { setShowLoginModal(true); return; }
    setShowLibraryPicker(true);
  };

  const handleLibraryPick = async (f: StagedLibraryFile) => {
    const fileName = stagedFileName(f);
    setStart(0);
    const base: VideoInfo = { name: f.name, objectUrl: f.downloadUrl, s3Key: f.key, s3Url: f.downloadUrl, duration: 0, status: 'uploaded' };
    if (!fileName.toLowerCase().endsWith('.mp4')) {
      setVideo({ ...base, status: 'converting' });
      convertClip(f.key);
      return;
    }
    setVideo(base);
    try {
      const meta = await probeVideoUrl(f.downloadUrl);
      setVideo(prev => prev ? { ...prev, duration: meta.duration, shortSide: Math.min(meta.width, meta.height) } : prev);
    } catch { /* duration is best-effort */ }
  };

  // ===== STEP 3: PROCESS =====
  const pollJob = useCallback((jobId: string, startedAt: number) => {
    const poll = async () => {
      setElapsed(Math.floor((Date.now() - startedAt) / 1000));
      try {
        const res = await fetch(`/api/generate-asset/status/${jobId}`);
        const data = await res.json();
        if (data.status === 'completed' && data.assetUrl) {
          try { localStorage.removeItem(PENDING_KEY); } catch { /* ignore */ }
          setFinalVideoUrl(data.assetUrl);
          setFinalLikeness(data.likeness || null);
          setProcessStatus('completed');
          // Go straight to the result; the toolbar's Continue button is easy to miss
          setCompletedSteps(prev => new Set([...prev, 'upload', 'describe', 'process']));
          setCurrentStep('download');
          return;
        }
        if (data.status === 'failed' || data.status === 'not_found') {
          try { localStorage.removeItem(PENDING_KEY); } catch { /* ignore */ }
          setProcessError(data.error || 'The edit failed. Your video tokens have been refunded.');
          setProcessStatus('failed');
          return;
        }
      } catch { /* network blip: keep polling */ }
      // Give up after an hour (Aleph usually takes 10-15 minutes)
      if (Date.now() - startedAt < 60 * 60 * 1000) pollTimer.current = setTimeout(poll, 5000);
      else { setProcessError('This is taking far longer than usual. Please try again.'); setProcessStatus('failed'); }
    };
    poll();
  }, []);

  const pollLong = useCallback((jobId: string, startedAt: number) => {
    const poll = async () => {
      setElapsed(Math.floor((Date.now() - startedAt) / 1000));
      try {
        const res = await fetch(`/api/scene-edit/long/${jobId}`, { headers: getAuthHeaders() });
        const data = await res.json();
        if (data.status === 'completed' && data.assetUrl) {
          try { localStorage.removeItem(PENDING_KEY); } catch { /* ignore */ }
          setLongProgress(null);
          setFinalVideoUrl(data.assetUrl);
          setProcessStatus('completed');
          setCompletedSteps(prev => new Set([...prev, 'upload', 'describe', 'process']));
          setCurrentStep('download');
          return;
        }
        if (data.status === 'failed' || data.status === 'not_found') {
          try { localStorage.removeItem(PENDING_KEY); } catch { /* ignore */ }
          setLongProgress(null);
          setProcessError(data.error ? `${data.error}. Your video tokens have been refunded.` : 'The scene failed. Your video tokens have been refunded.');
          setProcessStatus('failed');
          return;
        }
        if (data.pieces) setLongProgress({ done: data.piecesDone || 0, total: data.pieces, stitching: !!data.stitching });
      } catch { /* keep polling */ }
      if (Date.now() - startedAt < 120 * 60 * 1000) pollTimer.current = setTimeout(poll, 5000);
      else { setProcessError('This is taking far longer than usual. Please try again.'); setProcessStatus('failed'); }
    };
    poll();
  }, [getAuthHeaders]);

  // Resume a job left running when the tab closed
  useEffect(() => {
    try {
      const raw = localStorage.getItem(PENDING_KEY);
      if (!raw) return;
      const pending = JSON.parse(raw);
      if (!pending?.jobId || Date.now() - pending.startedAt > (pending.long ? 120 : 60) * 60 * 1000) {
        localStorage.removeItem(PENDING_KEY);
        return;
      }
      setPrompt(pending.prompt || '');
      setCompletedSteps(new Set(['upload', 'describe']));
      setCurrentStep('process');
      setProcessStatus('processing');
      if (pending.long) pollLong(pending.jobId, pending.startedAt);
      else pollJob(pending.jobId, pending.startedAt);
    } catch { /* ignore */ }
    return () => { if (pollTimer.current) clearTimeout(pollTimer.current); };
  }, [pollJob, pollLong]);


  const startLong = async () => {
    if (!video || !guideMatches || !guide?.key) return;
    setLongConfirm(false);
    markDoneAndGo('describe', 'process');
    setProcessStatus('starting');
    setProcessError('');
    setLongProgress(longQuote ? { done: 0, total: longQuote.pieces, stitching: false } : null);
    try {
      const res = await fetch('/api/scene-edit/long', {
        method: 'POST', headers: getAuthHeaders(),
        body: JSON.stringify({
          videoUrl: video.s3Url, videoKey: video.s3Key, prompt: prompt.trim(), keepPeople,
          roomMode, wearing: roomMode ? wearing.trim() : undefined,
          startTime: start, endTime: longEnd, sourceDuration: duration || undefined, guideKey: guide.key, lightSide,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to start the scene');
      setRenderGuideKey(guide.key);
      const startedAt = Date.now();
      try { localStorage.setItem(PENDING_KEY, JSON.stringify({ jobId: data.jobId, startedAt, prompt: prompt.trim(), long: true })); } catch { /* ignore */ }
      setProcessStatus('processing');
      pollLong(data.jobId, startedAt);
    } catch (err: any) {
      setProcessError(err.message);
      setProcessStatus('failed');
    }
  };

  // Price for the chosen range, from the same split the server will use
  useEffect(() => {
    if (!longMode || longLen <= LONG_MIN || longLen > LONG_MAX + 0.5) { setLongQuote(null); return; }
    const t = setTimeout(() => {
      fetch(`/api/scene-edit/long/quote?seconds=${longLen.toFixed(2)}`, { headers: getAuthHeaders() })
        .then(r => (r.ok ? r.json() : null)).then(d => setLongQuote(d && d.cost != null ? d : null)).catch(() => setLongQuote(null));
    }, 250);
    return () => clearTimeout(t);
  }, [longMode, longLen, getAuthHeaders]);

  const startEdit = async () => {
    if (!video) return;
    markDoneAndGo('describe', 'process');
    setProcessStatus('starting');
    setProcessError('');
    try {
      const res = await fetch('/api/scene-edit', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          videoUrl: video.s3Url,
          videoKey: video.s3Key,
          prompt: prompt.trim(),
          seconds: tier.seconds,
          startTime: start,
          ...(lineSel ? { length: lineSel.len, lineMode: true } : {}),
          sourceDuration: duration || undefined,
          keepPeople,
          roomMode,
          wearing: roomMode ? wearing.trim() : undefined,
          guideKey: activeGuideKey,
          lightSide,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.details || data.error || 'Failed to start the edit');
      setRenderGuideKey(activeGuideKey);
      setRenderedLine(lineSel ? { start: lineSel.start, len: lineSel.len } : null);
      const startedAt = Date.now();
      try { localStorage.setItem(PENDING_KEY, JSON.stringify({ jobId: data.jobId, startedAt, prompt: prompt.trim() })); } catch { /* ignore */ }
      setProcessStatus('processing');
      pollJob(data.jobId, startedAt);
    } catch (err: any) {
      setProcessError(err.message);
      setProcessStatus('failed');
    }
  };

  // Swaps by line: the clip's lines from its transcript (made on first ask; the page polls)
  const loadLines = async () => {
    if (!video?.s3Key) return;
    const key = video.s3Key;
    setLines({ status: 'loading', items: [] });
    const deadline = Date.now() + 4 * 60 * 1000;
    try {
      for (;;) {
        const r = await fetch(`/api/scene-edit/transcript?videoKey=${encodeURIComponent(key)}`, { headers: getAuthHeaders() });
        const d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(d.error || 'Could not read the lines.');
        if (d.status === 'ready') {
          const items: SceneLine[] = (d.lines || []).filter((l: SceneLine) => l.end - l.start > 0.05);
          setLines(items.length ? { status: 'ready', items } : { status: 'failed', items: [], error: 'We could not hear any lines in this clip.' });
          return;
        }
        if (Date.now() > deadline) throw new Error('Reading the lines is taking too long. Try again in a minute.');
        await new Promise(res => setTimeout(res, 4000));
      }
    } catch (err: any) {
      setLines({ status: 'failed', items: [], error: err.message });
    }
  };

  // Tap a line to render just that line; shift-tap another to take the run between them
  const pickLine = (i: number, extend: boolean) => {
    const first = extend && lineSel ? Math.min(lineSel.first, i) : i;
    const last = extend && lineSel ? Math.max(lineSel.last, i) : i;
    const items = lines.items;
    const cap = duration || Infinity;
    let a = Math.max(0, items[first].start - LINE_PAD);
    let b = Math.min(cap, items[last].end + LINE_PAD);
    if (b - a < LINE_MIN) { b = Math.min(cap, a + LINE_MIN); a = Math.max(0, b - LINE_MIN); }
    if (b - a > LINE_MAX) b = a + LINE_MAX;
    setLongMode(false);
    setLineSel({ first, last, start: a, len: b - a });
    setStart(a);
    if (previewRef.current) previewRef.current.currentTime = a;
  };

  const handleStartEdit = async () => {
    if (!prompt.trim() || !video) return;
    if (!getAuthToken()) { setShowLoginModal(true); return; }
    const creditCheck = await checkCredits(tier.op);
    if (creditCheck?.isFree) { startEdit(); return; }
    if (!subscriptionLoading && !isSubscriber) { setShowUpgradeModal(true); return; }
    if (creditCheck?.hasCredits) {
      confirmCredit(tier.op as any, `Scene Edit (${tier.seconds}s)`, startEdit);
    } else {
      toast({ title: 'Not enough video tokens', description: `A ${tier.seconds}-second scene edit takes ${tier.credits} video tokens.`, variant: 'destructive' });
    }
  };

  // ===== PROMPT HELPER =====
  const improvePrompt = async () => {
    const idea = prompt.trim();
    if (!idea) return;
    setImproving(true);
    try {
      const res = await fetch('/api/scene-edit/improve-prompt', {
        method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ prompt: idea, mode: promptMode }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.prompt) throw new Error(data.error || 'Could not improve that prompt right now.');
      setPrompt(data.prompt);
      setImproved({ from: idea, note: data.note || '' });
    } catch (err: any) {
      toast({ title: 'Prompt helper', description: err.message, variant: 'destructive' });
    } finally {
      setImproving(false);
    }
  };

  // ===== WHAT THEY'RE WEARING (room mode locks the clothes by name) =====
  useEffect(() => {
    if (currentStep !== 'describe' || !video || video.status !== 'uploaded' || !video.s3Key) return;
    if (wearingFor.current === video.s3Key) return;
    wearingFor.current = video.s3Key;
    setWearingStatus('reading');
    fetch('/api/scene-edit/describe-wearing', {
      method: 'POST', headers: getAuthHeaders(),
      body: JSON.stringify({ videoUrl: video.s3Url, videoKey: video.s3Key, startTime: start }),
    })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d?.wearing) setWearing(w => w.trim() ? w : d.wearing); })
      .catch(() => {})
      .finally(() => setWearingStatus('done'));
  }, [currentStep, video, start, getAuthHeaders]);

  // ===== GUIDE FRAME =====
  const guideMatches = !!guide && guide.status === 'ready' && guide.start === start;
  const activeGuideKey = guideMatches && useGuide ? guide!.key : undefined;

  // ===== SHOT CHECK (free) =====
  // The start frame: is the face clear enough for the edit to keep it, and which side
  // is the light on? Re-checked when the start point settles. Never blocks.
  const shotKey = video?.s3Key && video.status === 'uploaded' ? `${video.s3Key}@${start}` : '';
  const lightSide = shot.for === shotKey ? shot.result?.light?.side : undefined;
  useEffect(() => {
    if (currentStep !== 'describe' || !shotKey || shot.for === shotKey || !getAuthToken()) return;
    const t = setTimeout(() => {
      setShot({ checking: true, for: shotKey, result: null });
      fetch('/api/scene-edit/check-shot', {
        method: 'POST', headers: getAuthHeaders(),
        body: JSON.stringify({ videoUrl: video!.s3Url, videoKey: video!.s3Key, startTime: start }),
      })
        .then(r => (r.ok ? r.json() : null))
        .then(d => setShot(prev => (prev.for === shotKey ? { checking: false, for: shotKey, result: d } : prev)))
        .catch(() => setShot(prev => (prev.for === shotKey ? { checking: false, for: shotKey, result: null } : prev)));
    }, 700);
    return () => clearTimeout(t);
  }, [currentStep, shotKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // ===== SAVED SETS =====
  const selectedSet = sets.find(x => x.id === selectedSetId) || null;
  const loadSets = useCallback(() => {
    if (!getAuthToken()) return;
    fetch('/api/scene-sets', { headers: getAuthHeaders() })
      .then(r => (r.ok ? r.json() : null)).then(d => { if (d?.sets) setSets(d.sets); }).catch(() => {});
  }, [getAuthHeaders, getAuthToken]);
  useEffect(() => { loadSets(); }, [loadSets]);
  const pickSet = (x: SceneSet | null) => {
    setSelectedSetId(x?.id || null);
    if (x) { setPromptMode(x.promptMode); setPrompt(x.prompt); setImproved(null); }
  };
  const saveSet = async (guideKey: string, name: string) => {
    const r = await fetch('/api/scene-sets', {
      method: 'POST', headers: getAuthHeaders(),
      body: JSON.stringify({ guideKey, name, prompt: prompt.trim(), promptMode, lightSide }),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(d.error || 'Could not save the set.');
    setSets(prev => [d.set, ...prev]);
    setSavedGuideKeys(prev => [...prev, guideKey]);
  };
  const renameSet = async (id: string, name: string) => {
    const r = await fetch(`/api/scene-sets/${id}`, { method: 'PATCH', headers: getAuthHeaders(), body: JSON.stringify({ name }) });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) { toast({ title: 'Rename failed', description: d.error || 'Try again.', variant: 'destructive' }); return; }
    setSets(prev => prev.map(x => (x.id === id ? d.set : x)));
  };
  const deleteSet = async (id: string) => {
    const r = await fetch(`/api/scene-sets/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
    if (!r.ok) { toast({ title: 'Delete failed', description: 'Try again.', variant: 'destructive' }); return; }
    setSets(prev => prev.filter(x => x.id !== id));
    if (selectedSetId === id) setSelectedSetId(null);
  };

  const addRefPhoto = async (file: File) => {
    if (refPhotos.length >= 2) return;
    setRefUploading(true);
    try {
      const res = await fetch('/get-upload-url', {
        method: 'POST', headers: getAuthHeaders(),
        body: JSON.stringify({ fileName: file.name, fileType: file.type || 'image/jpeg', ephemeral: true }),
      });
      if (!res.ok) throw new Error('Failed to get upload URL');
      const { uploadUrl, key } = await res.json();
      const put = await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': file.type || 'image/jpeg' }, body: file });
      if (!put.ok) throw new Error(`Upload failed: ${put.status}`);
      setRefPhotos(prev => [...prev, { key, url: URL.createObjectURL(file) }]);
    } catch (err: any) {
      toast({ title: 'Photo upload failed', description: err.message, variant: 'destructive' });
    } finally {
      setRefUploading(false);
    }
  };

  const makeGuide = async () => {
    if (!video) return;
    const at = start;
    const setId = selectedSet?.id;
    setGuide({ status: 'making', start: at, fromSet: !!setId });
    setUseGuide(true);
    try {
      const res = await fetch('/api/scene-edit/guide-frame', {
        method: 'POST', headers: getAuthHeaders(),
        body: JSON.stringify({ videoUrl: video.s3Url, videoKey: video.s3Key, prompt: prompt.trim(), startTime: at, referenceKeys: setId ? [] : refPhotos.map(r => r.key), lightSide, setId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Could not make the guide frame.');
      // The frame renders in the background (a minute or two); check back until it's done.
      // A dropped check (phone locked, flaky signal) just waits for the next one.
      const deadline = Date.now() + 8 * 60 * 1000;
      for (;;) {
        await new Promise(r => setTimeout(r, 3000));
        if (Date.now() > deadline) throw new Error('The guide frame is taking too long. Try again.');
        let s: any;
        try {
          const r = await fetch(`/api/scene-edit/guide-frame/${data.jobId}`, { headers: getAuthHeaders() });
          if (r.status === 404) throw new Error('Could not make the guide frame.');
          if (!r.ok) continue;
          s = await r.json();
        } catch (e: any) {
          if (e?.message === 'Could not make the guide frame.') throw e;
          continue;
        }
        if (s.status === 'ready') {
          setGuide({ status: 'ready', key: s.guideKey, url: s.guideUrl, start: at, fromSet: !!setId });
          return;
        }
        if (s.status === 'failed') throw new Error(s.error || 'Could not make the guide frame.');
      }
    } catch (err: any) {
      setGuide({ status: 'failed', start: at, error: err.message });
    }
  };

  const handleMakeGuide = async () => {
    if (!prompt.trim() || !video) return;
    if (!getAuthToken()) { setShowLoginModal(true); return; }
    if (selectedSet) { makeGuide(); return; }   // a saved set's still is free
    const op = CREDIT_OPERATIONS.SCENE_GUIDE_FRAME;
    const creditCheck = await checkCredits(op);
    if (creditCheck?.isFree) { makeGuide(); return; }
    if (!subscriptionLoading && !isSubscriber) { setShowUpgradeModal(true); return; }
    if (creditCheck?.hasCredits) confirmCredit(op as any, 'Scene Edit guide frame', makeGuide);
    else toast({ title: 'Not enough video tokens', description: 'A guide frame takes 1 video token.', variant: 'destructive' });
  };

  // ===== PREVIEW (5s) =====
  const startPreview = async () => {
    if (!video) return;
    if (previewTimer.current) clearTimeout(previewTimer.current);
    const asked = { prompt: prompt.trim(), start, keepPeople, guideKey: activeGuideKey, wearing: roomMode ? wearing.trim() : '' };
    const startedAt = Date.now();
    setPreview({ status: 'running', startedAt, ...asked });
    setPreviewElapsed(0);
    try {
      const res = await fetch('/api/scene-edit', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          videoUrl: video.s3Url, videoKey: video.s3Key, prompt: asked.prompt,
          seconds: PREVIEW.seconds, startTime: start, sourceDuration: duration || undefined, keepPeople,
          roomMode, wearing: roomMode ? wearing.trim() : undefined,
          guideKey: activeGuideKey, lightSide,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.details || data.error || 'Failed to start the preview');
      const poll = async () => {
        setPreviewElapsed(Math.floor((Date.now() - startedAt) / 1000));
        try {
          const r = await fetch(`/api/generate-asset/status/${data.jobId}`);
          const st = await r.json();
          if (st.status === 'completed' && st.assetUrl) {
            setPreview(p => p ? { ...p, status: 'done', url: st.assetUrl, likeness: st.likeness || null } : p);
            setTimeout(() => previewCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 150);
            return;
          }
          if (st.status === 'failed' || st.status === 'not_found') {
            setPreview(p => p ? { ...p, status: 'failed', error: st.error || 'The preview failed. Your video tokens have been refunded.' } : p);
            return;
          }
        } catch { /* keep polling */ }
        if (Date.now() - startedAt < 30 * 60 * 1000) previewTimer.current = setTimeout(poll, 5000);
        else setPreview(p => p ? { ...p, status: 'failed', error: 'The preview is taking far longer than usual.' } : p);
      };
      poll();
    } catch (err: any) {
      setPreview(p => p ? { ...p, status: 'failed', error: err.message } : p);
    }
  };

  const handlePreview = async () => {
    if (!prompt.trim() || !video) return;
    if (!getAuthToken()) { setShowLoginModal(true); return; }
    const creditCheck = await checkCredits(PREVIEW.op);
    if (creditCheck?.isFree) { startPreview(); return; }
    if (!subscriptionLoading && !isSubscriber) { setShowUpgradeModal(true); return; }
    if (creditCheck?.hasCredits) {
      confirmCredit(PREVIEW.op as any, 'Scene Edit preview (5s)', startPreview);
    } else {
      toast({ title: 'Not enough video tokens', description: `A preview takes ${PREVIEW.credits} video tokens.`, variant: 'destructive' });
    }
  };

  const previewIsStale = !!preview && (preview.prompt !== prompt.trim() || preview.start !== start || preview.keepPeople !== keepPeople || preview.guideKey !== activeGuideKey || (preview.wearing || '') !== (roomMode ? wearing.trim() : ''));
  const previewFresh = preview?.status === 'done' && !!preview.url && !previewIsStale;
  const previewDrifted = previewFresh && preview?.likeness?.status === 'drift';
  const driftWord = (prompt.match(DRIFT_WORDS) || [])[0] || null;

  // Build up changes one at a time: the finished clip becomes the new source
  const switchPromptMode = (to: 'room' | 'look') => {
    if (to === promptMode) return;
    parkedPrompt.current[promptMode] = { prompt, improved, setId: selectedSetId };
    const back = parkedPrompt.current[to];
    setPromptMode(to);
    setPrompt(back.prompt);
    setImproved(back.improved);
    setSelectedSetId(back.setId);
  };

  // Put the description, start point and outfit line back to what the preview was made from.
  const restorePreviewed = () => {
    if (!preview) return;
    setPrompt(preview.prompt);
    setStart(preview.start);
    setKeepPeople(preview.keepPeople);
    if (roomMode) setWearing(preview.wearing || '');
    setImproved(null);
  };

  const keepEditingResult = () => {
    const key = finalVideoUrl.startsWith('/api/s3/') ? decodeURIComponent(finalVideoUrl.slice('/api/s3/'.length)) : '';
    setVideo({ name: `Scene edit - ${prompt.trim().slice(0, 40)}`, objectUrl: finalVideoUrl, s3Key: key, s3Url: finalVideoUrl, duration: 0, status: 'uploaded' });
    const temp = document.createElement('video');
    temp.preload = 'metadata';
    temp.src = finalVideoUrl;
    temp.onloadedmetadata = () => setVideo(prev => prev ? { ...prev, duration: temp.duration } : prev);
    setPrompt('');
    setStart(0);
    setPreview(null);
    setProcessStatus('idle');
    setFinalVideoUrl(''); setFinalLikeness(null);
    setCompletedSteps(new Set(['upload']));
    setCurrentStep('describe');
  };

  // Continue this scene: edit the next stretch of the same source clip, starting from
  // the last frame of this result so the setting carries across, then join the parts.
  type ScenePart = { s3Key: string; start: number; length: number };
  const [sceneChain, setSceneChain] = useState<{ sourceKey: string; parts: ScenePart[]; byLine?: boolean } | null>(null);
  const [continuing, setContinuing] = useState(false);
  const [joinState, setJoinState] = useState<{ status: 'joining' | 'failed'; error?: string } | null>(null);
  const resultKeyOf = (url: string) => (url.startsWith('/api/s3/') ? decodeURIComponent(url.slice('/api/s3/'.length).split('?')[0]) : '');

  const continueScene = async (resultKey: string) => {
    if (!resultKey) return;
    setContinuing(true);
    try {
      const res = await fetch('/api/scene-edit/continue', { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ resultKey }) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || 'Could not set up the next part.');
      const remaining = (d.sourceDuration || 0) - d.nextStart;
      const nextTier = (TIERS.find(t => t.seconds >= remaining) || TIERS[TIERS.length - 1]).seconds;
      const nextGuide = { status: 'ready' as const, key: d.guideKey, url: d.guideUrl, start: d.nextStart };
      pendingGuideRef.current = nextGuide;
      setGuide(nextGuide);
      setUseGuide(true);
      setVideo({ name: 'Original clip', objectUrl: d.sourceUrl, s3Key: d.sourceKey, s3Url: d.sourceUrl, duration: d.sourceDuration || 0, status: 'uploaded' });
      // Continuing from part 2+ of a chain we're building keeps the earlier parts
      const extending = canJoin && resultKey === currentResultKey;
      setSceneChain({
        sourceKey: d.sourceKey,
        parts: extending ? [...sceneChain!.parts, { s3Key: resultKey, start, length: d.nextStart - start }] : (d.parts || []),
      });
      setLongMode(false);
      setTierSeconds(nextTier as 10 | 20 | 30);
      setStart(d.nextStart);
      setPrompt(d.prompt || prompt);
      setKeepPeople(d.keepPeople !== false);
      setPreview(null);
      setJoinState(null);
      setProcessStatus('idle');
      setFinalVideoUrl(''); setFinalLikeness(null);
      setCompletedSteps(new Set(['upload']));
      setCurrentStep('describe');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: any) {
      toast({ title: 'Could not continue this scene', description: err.message, variant: 'destructive' });
    } finally {
      setContinuing(false);
    }
  };

  // The result on screen is the next part of the chain when it starts where the chain ends
  const currentResultKey = resultKeyOf(finalVideoUrl);
  const chainEnd = sceneChain?.parts.length ? sceneChain.parts[sceneChain.parts.length - 1].start + sceneChain.parts[sceneChain.parts.length - 1].length : null;
  const lastPart = sceneChain?.parts.length ? sceneChain.parts[sceneChain.parts.length - 1] : null;
  // Line swaps join any later line of the same clip: the server cuts overlaps between
  // the words and fills gaps with the original footage.
  const canJoin = !!sceneChain && !!currentResultKey && !sceneChain.parts.some(p => p.s3Key === currentResultKey)
    && chainEnd != null && video?.s3Key === sceneChain.sourceKey
    && (sceneChain.byLine ? !!renderedLine && !!lastPart && renderedLine.start > lastPart.start : Math.abs(chainEnd - start) < 0.2);

  // Swap another line: keep this line's render as a part and go back to pick the next line
  const swapAnotherLine = () => {
    if (!video?.s3Key || !currentResultKey || !renderedLine) return;
    const part = { s3Key: currentResultKey, start: renderedLine.start, length: renderedLine.len };
    setSceneChain(prev => (prev?.byLine && prev.sourceKey === video.s3Key
      ? { ...prev, parts: prev.parts.some(p => p.s3Key === part.s3Key) ? prev.parts : [...prev.parts, part] }
      : { sourceKey: video.s3Key, parts: [part], byLine: true }));
    setLineSel(null);
    setPreview(null);
    setJoinState(null);
    setProcessStatus('idle');
    setFinalVideoUrl(''); setFinalLikeness(null);
    setCompletedSteps(new Set(['upload']));
    setCurrentStep('describe');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const joinParts = async () => {
    if (!sceneChain || !currentResultKey) return;
    setJoinState({ status: 'joining' });
    try {
      const partKeys = [...sceneChain.parts.map(p => p.s3Key), currentResultKey];
      const res = await fetch('/api/scene-edit/join', { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ partKeys }) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || 'Could not join the parts.');
      const deadline = Date.now() + 10 * 60 * 1000;
      for (;;) {
        await new Promise(r => setTimeout(r, 3000));
        if (Date.now() > deadline) throw new Error('Joining is taking too long. Try again.');
        let st: any = null;
        try { const r = await fetch(`/api/scene-edit/join/${d.jobId}`, { headers: getAuthHeaders() }); if (r.ok) st = await r.json(); } catch { /* retry */ }
        if (!st) continue;
        if (st.status === 'ready') {
          setFinalVideoUrl(st.assetUrl);
          setSceneChain(null);
          setJoinState(null);
          toast({ title: 'Parts joined', description: 'The full scene is saved to My Library.' });
          return;
        }
        if (st.status === 'failed') throw new Error(st.error || 'Could not join the parts.');
      }
    } catch (err: any) {
      setJoinState({ status: 'failed', error: err.message });
    }
  };

  // Past Scene Edit results from My Library, shown on the Download step so earlier
  // edits are one tap away (they autosave there; Joe, Oct 10: "don't even link to it").
  const [sceneLibrary, setSceneLibrary] = useState<{ id: string; name: string; s3Key: string; createdAt?: string }[]>([]);
  useEffect(() => {
    if (currentStep !== 'download') return;
    let cancelled = false;
    fetch('/api/my-library', { headers: getAuthHeaders() })
      .then(r => (r.ok ? r.json() : { items: [] }))
      .then(d => {
        if (cancelled) return;
        const mine = (d.items || [])
          .filter((i: any) => i.s3Key && /^(Scene edit|Runway video)/.test(i.name || ''))
          .slice(0, 12)
          .map((i: any) => ({ id: String(i._id), name: i.name, s3Key: i.s3Key, createdAt: i.createdAt }));
        setSceneLibrary(mine);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [currentStep, getAuthHeaders]);

  const handleDownload = async () => {
    const a = document.createElement('a');
    a.href = finalVideoUrl;
    a.download = `scene-edit-${Date.now()}.mp4`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  // ===== STEP ADVANCE =====
  const advance: { onAdvance: () => void; label?: string; disabled?: boolean; showAdvance?: boolean } = (() => {
    switch (currentStep) {
      case 'upload':
        return {
          onAdvance: () => markDoneAndGo('upload', 'describe'),
          label: video?.status === 'converting' ? 'Converting…' : video?.status === 'uploading' ? 'Uploading…' : 'Continue',
          disabled: !video || video.status !== 'uploaded',
        };
      case 'describe':
        if (longMode) {
          const ready = !!prompt.trim() && !!longQuote && guideMatches && useGuide;
          return {
            onAdvance: () => { if (!ready) return; if (longConfirm) startLong(); else setLongConfirm(true); },
            label: !guideMatches || !useGuide ? 'Make a guide frame first'
              : !longQuote ? 'Pick a 30s to 2 min range'
              : longConfirm ? `Tap again to spend ${longQuote.cost} video tokens`
              : `Edit ${fmt(longLen)} scene · ${longQuote.cost} video tokens`,
            disabled: !ready,
          };
        }
        // Cheap path first: until there's a preview of exactly this prompt, the main
        // button makes one. "Skip the preview" under it renders straight away.
        if (shortRender) {
          return {
            onAdvance: handleStartEdit,
            label: `Render ${shortLen}s · ${tier.credits} video tokens`,
            disabled: !prompt.trim() || (duration > 0 && end - start < 2),
          };
        }
        if (!previewFresh) {
          return {
            onAdvance: handlePreview,
            label: preview?.status === 'running' ? 'Previewing…' : `Preview ${PREVIEW.seconds}s first · ${PREVIEW.credits} video tokens`,
            disabled: !prompt.trim() || preview?.status === 'running' || (duration > 0 && end - start < 2),
          };
        }
        if (previewDrifted) {
          return { onAdvance: () => {}, label: 'Faces changed. Adjust the description, then preview again', disabled: true };
        }
        return {
          onAdvance: handleStartEdit,
          label: `Render ${tier.seconds}s · ${tier.credits} video tokens`,
          disabled: !prompt.trim() || (duration > 0 && end - start < 2),
        };
      case 'process':
        if (processStatus === 'completed') return { onAdvance: () => markDoneAndGo('process', 'download'), label: 'Continue to Download' };
        if (processStatus === 'failed') return { onAdvance: () => { setProcessStatus('idle'); setCurrentStep('describe'); }, label: 'Try again' };
        return { onAdvance: () => {}, label: 'Editing…', disabled: true };
      default:
        return { onAdvance: () => {}, showAdvance: false };
    }
  })();

  // ===== RENDER =====
  const renderUploadStep = () => (
    <StepCard>
      <StepHeader
        kicker="Step 01 · Upload"
        title="Drop in your video."
        subtitle="One clip. You'll pick which 10, 20 or 30 seconds to change next."
      />
      <div
        {...getRootProps()}
        className="flex items-center gap-3 cursor-pointer transition-colors"
        style={{
          padding: '18px 20px',
          borderRadius: 'var(--hj-r-md)',
          border: `1.5px dashed ${isDragActive ? 'var(--hj-blue)' : 'var(--hj-hairline)'}`,
          background: isDragActive ? 'var(--hj-blue-tint)' : 'var(--hj-surface-2)',
        }}
      >
        <input {...getInputProps()} />
        <div
          className="flex items-center justify-center flex-shrink-0"
          style={{ width: 40, height: 40, borderRadius: 'var(--hj-r-sm)', background: 'var(--hj-surface-3)', color: 'var(--hj-ink-2)' }}
        >
          <Upload size={18} strokeWidth={1.8} />
        </div>
        <div>
          <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--hj-ink)', marginBottom: 2 }}>Drop or browse a clip</div>
          <div style={{ fontSize: 12.5, color: 'var(--hj-ink-2)' }}>MP4 · MOV · WebM · AVI · MKV — one file.</div>
        </div>
      </div>

      <button
        type="button"
        onClick={openLibraryPicker}
        className="inline-flex items-center gap-2"
        style={{
          marginTop: 10, padding: '8px 12px', borderRadius: 'var(--hj-r-sm)', border: '1px solid var(--hj-hairline)',
          background: 'transparent', color: 'var(--hj-ink-2)', fontSize: 12.5, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer',
        }}
      >
        <FolderOpen size={14} strokeWidth={1.8} />
        Choose from your library
      </button>

      <ShootCoachCard />

      {video && (
        <div style={{ marginTop: 18 }}>
          <div
            className="flex items-center justify-between gap-3"
            style={{ padding: '12px 14px', borderRadius: 'var(--hj-r-sm)', background: 'var(--hj-surface-2)', border: '1px solid var(--hj-hairline-soft)', marginBottom: 14 }}
          >
            <span className="truncate" style={{ fontSize: 13.5, color: 'var(--hj-ink)', fontWeight: 500 }}>{video.name}</span>
            <span className="mono-label flex-shrink-0">
              {video.status === 'uploading' && `Uploading ${video.uploadProgress ?? 0}%`}
              {video.status === 'converting' && 'Converting…'}
              {video.status === 'uploaded' && (duration ? `Ready · ${fmt(duration)}` : 'Ready')}
              {video.status === 'error' && 'Failed'}
            </span>
          </div>
          {/* Oct 30 2026: a 720p clip of Katia came back with a different face; the
              same edit on her 1080p footage held. Warn, don't block. */}
          {!!video.shortSide && video.shortSide < 1080 && (
            <p style={{ fontSize: 13.5, color: 'var(--hj-ink)', lineHeight: 1.5, margin: '0 0 14px', padding: '10px 14px', borderRadius: 'var(--hj-r-sm)', background: 'var(--hj-surface-2)', border: '1px solid var(--hj-hairline)' }}>
              This clip is {video.shortSide}p. Faces hold better at 1080p or higher, so if you have a sharper version, use that one.
            </p>
          )}
          <div className="aspect-video overflow-hidden" style={{ background: 'oklch(0.08 0.005 70)', borderRadius: 'var(--hj-r-md)', border: '1px solid var(--hj-hairline)' }}>
            <video src={video.objectUrl} className="w-full h-full object-contain" controls preload="metadata" playsInline />
          </div>
        </div>
      )}

    </StepCard>
  );

  const renderDescribeStep = () => (
    <StepCard>
      <StepHeader
        kicker="Step 02 · Describe"
        title="Say what should change."
        subtitle="Change the place and the light around you. Your performance stays the same."
      />

      {/* How to spend well: preview first, change one thing at a time, then render.
          Copy written by GPT from a fact sheet (Oct 10). */}
      {!shortRender && <div style={{ marginBottom: 18, padding: '14px 16px', borderRadius: 'var(--hj-r-md)', background: 'var(--hj-surface-2)', border: '1px solid var(--hj-hairline-soft)' }}>
        <div style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--hj-ink)', marginBottom: 10 }}>Spend a little first, then render</div>
        <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {GUIDE_STEPS.map((g, i) => (
            <li key={g.title} style={{ display: 'grid', gridTemplateColumns: '22px minmax(0, 1fr)', gap: 6, fontSize: 13.5, lineHeight: 1.45, color: 'var(--hj-ink)' }}>
              <span className="mono-label" style={{ paddingTop: 1 }}>{i + 1}</span>
              <span><strong style={{ fontWeight: 600 }}>{g.title} · {g.cost || `${longMode ? (longQuote ? `${longQuote.cost} video tokens` : 'priced by length') : `${tier.credits} video tokens`}`}.</strong> {g.line}</span>
            </li>
          ))}
        </ol>
        {!longMode && (
          <p style={{ margin: '10px 0 0', fontSize: 13.5, fontWeight: 600, color: 'var(--hj-ink)' }}>
            You'll usually spend {tier.credits + 2 * PREVIEW.credits} to {tier.credits + 3 * PREVIEW.credits} video tokens for a finished {tier.seconds}-second scene.
          </p>
        )}
      </div>}

      <div className="mono-label" style={{ marginBottom: 8 }}>Length</div>
      <div className={`grid ${canLong ? 'grid-cols-2 sm:grid-cols-5' : 'grid-cols-2 sm:grid-cols-4'} gap-2`} style={{ marginBottom: 18 }}>
        {TIERS.map(t => (
          <button
            key={t.seconds}
            type="button"
            onClick={() => { setLongMode(false); setLongConfirm(false); setLineSel(null); setTierSeconds(t.seconds); setStart(s => Math.min(s, Math.max(0, duration - t.seconds))); }}
            className="text-left transition-colors"
            style={pillButton(!longMode && !lineSel && tierSeconds === t.seconds)}
          >
            <div style={{ fontSize: 13.5, fontWeight: 600 }}>{t.seconds} seconds</div>
            <div style={{ fontSize: 11.5, color: 'var(--hj-ink-2)', marginTop: 2 }}>{t.credits} video tokens</div>
          </button>
        ))}
        {canLong && (
          <button
            type="button"
            onClick={() => {
              setLongMode(true);
              setLongConfirm(false);
              setLineSel(null);
              const s0 = Math.min(start, Math.max(0, duration - LONG_MIN - 1));
              setStart(s0);
              setLongEnd(Math.min(duration, s0 + LONG_MAX));
            }}
            className="text-left transition-colors"
            style={pillButton(longMode)}
          >
            <div style={{ fontSize: 13.5, fontWeight: 600 }}>Whole scene</div>
            <div style={{ fontSize: 11.5, color: 'var(--hj-ink-2)', marginTop: 2 }}>Up to 2 minutes</div>
          </button>
        )}
      </div>
      {longMode && (
        <p style={{ fontSize: 12.5, color: 'var(--hj-ink-2)', margin: '-8px 0 16px', lineHeight: 1.45 }}>
          Each edit covers up to 30 seconds, so we edit the scene in pieces and join them with your original audio.
          Make a guide frame below first: every piece matches it, so the setting stays the same across the cuts.
          {longQuote && ` This range is ${longQuote.pieces} pieces, ${longQuote.cost} video tokens.`}
        </p>
      )}

      {video && (
        <div className="aspect-video overflow-hidden" style={{ background: 'oklch(0.08 0.005 70)', borderRadius: 'var(--hj-r-md)', border: '1px solid var(--hj-hairline)', marginBottom: 12 }}>
          <video ref={previewRef} src={video.objectUrl} className="w-full h-full object-contain" controls preload="metadata" playsInline />
        </div>
      )}

      {longMode && (
        <div style={{ marginBottom: 18 }}>
          <div className="mono-label" style={{ marginBottom: 8 }}>Editing {fmt(start)} to {fmt(longEnd)} ({fmt(longLen)})</div>
          <Slider
            value={[start, longEnd]}
            min={0}
            max={duration}
            step={0.5}
            minStepsBetweenThumbs={1}
            onValueChange={([a, b]) => {
              let s2 = a, e2 = b;
              if (e2 - s2 > LONG_MAX) { if (s2 !== start) e2 = s2 + LONG_MAX; else s2 = e2 - LONG_MAX; }
              if (e2 - s2 < LONG_MIN + 0.5) { if (s2 !== start) s2 = Math.max(0, e2 - LONG_MIN - 0.5); else e2 = Math.min(duration, s2 + LONG_MIN + 0.5); }
              setLongConfirm(false);
              setStart(s2);
              setLongEnd(e2);
              if (previewRef.current && s2 !== start) previewRef.current.currentTime = s2;
            }}
          />
        </div>
      )}
      {!longMode && maxStart > 0 && (
        <div style={{ marginBottom: 18 }}>
          <div className="mono-label" style={{ marginBottom: 8 }}>Editing {fmt(start)} to {fmt(end)}</div>
          <Slider
            value={[start]}
            min={0}
            max={maxStart}
            step={0.5}
            onValueChange={([v]) => {
              setLineSel(null);
              setStart(v);
              if (previewRef.current) previewRef.current.currentTime = v;
            }}
          />
        </div>
      )}
      {!longMode && video?.s3Key && video.status === 'uploaded' && (
        <div style={{ marginBottom: 18, padding: '14px 16px', borderRadius: 'var(--hj-r-md)', border: '1px solid var(--hj-hairline)', background: 'var(--hj-surface-1)' }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--hj-ink)' }}>Or pick a line</div>
          <div style={{ fontSize: 12.5, color: 'var(--hj-ink-2)', marginTop: 2, lineHeight: 1.45 }}>
            Put a new place on one line. We render just that line and cut between the words. Shift-tap another line to take everything in between.
          </div>
          {lines.status === 'idle' && (
            <button type="button" onClick={loadLines} className="inline-flex items-center gap-1.5"
              style={{ marginTop: 10, minHeight: 40, padding: '8px 14px', borderRadius: 999, border: '1px solid var(--hj-hairline)', background: 'transparent', color: 'var(--hj-ink)', fontSize: 13, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer' }}>
              Show the lines · free
            </button>
          )}
          {lines.status === 'loading' && (
            <div className="inline-flex items-center gap-2" style={{ marginTop: 10, fontSize: 12.5, color: 'var(--hj-ink-2)' }}>
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Listening to your clip. Usually under a minute.
            </div>
          )}
          {lines.status === 'failed' && (
            <p style={{ fontSize: 12.5, color: 'hsl(var(--destructive))', margin: '10px 0 0' }}>{lines.error}</p>
          )}
          {lines.status === 'ready' && (
            <div className="flex flex-col gap-1.5" style={{ marginTop: 10, maxHeight: 260, overflowY: 'auto' }}>
              {lines.items.map((l, i) => {
                const on = !!lineSel && i >= lineSel.first && i <= lineSel.last;
                return (
                  <button key={`${l.start}-${i}`} type="button" onClick={(e) => pickLine(i, e.shiftKey)} className="text-left"
                    style={{ ...pillButton(on), padding: '8px 10px', display: 'grid', gridTemplateColumns: '44px minmax(0, 1fr)', gap: 8, fontSize: 13, lineHeight: 1.4 }}>
                    <span className="mono-label" style={{ paddingTop: 1 }}>{fmt(l.start)}</span>
                    <span>{l.text}</span>
                  </button>
                );
              })}
            </div>
          )}
          {lineSel && (
            <p style={{ margin: '10px 0 0', fontSize: 12.5, fontWeight: 600, color: 'var(--hj-ink)' }}>
              Editing {fmt(lineSel.start)} to {fmt(lineSel.start + lineSel.len)} ({lineSel.len.toFixed(1)}s) · {tier.credits} video tokens.{' '}
              <button type="button" onClick={() => setLineSel(null)} style={{ background: 'none', border: 0, padding: 0, color: 'var(--hj-ink-2)', textDecoration: 'underline', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5 }}>Clear</button>
            </p>
          )}
        </div>
      )}

      {duration > 0 && duration < tierSeconds && (
        <p style={{ fontSize: 12.5, color: 'var(--hj-ink-2)', margin: '0 0 18px' }}>
          Your clip is {Math.round(duration)} seconds, so the whole clip gets edited.
        </p>
      )}

      <ShotCheckCard checking={shot.checking && shot.for === shotKey} result={shot.for === shotKey ? shot.result : null} at={fmt(start)} />

      <SavedSetsPicker sets={sets} selectedId={selectedSetId} onPick={pickSet} onRename={renameSet} onDelete={deleteSet} />

      <div className="grid grid-cols-2 gap-2" style={{ marginBottom: 14 }}>
        <button
          type="button"
          onClick={() => switchPromptMode('room')}
          style={{ ...pillButton(promptMode === 'room'), padding: '10px 12px', fontSize: 13.5, fontWeight: 600, textAlign: 'center' }}
        >
          New room
        </button>
        <button
          type="button"
          onClick={() => switchPromptMode('look')}
          style={{ ...pillButton(promptMode === 'look'), padding: '10px 12px', fontSize: 13.5, fontWeight: 600, textAlign: 'center' }}
        >
          Quick looks
        </button>
      </div>

      {roomMode ? (
        <>
          <div className="mono-label" style={{ marginBottom: 8 }}>Pick a room</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" style={{ marginBottom: 18 }}>
            {ROOMS.map(r => (
              <button
                key={r.key}
                type="button"
                onClick={() => { setPrompt(r.room); setImproved(null); setSelectedSetId(null); }}
                className="text-left transition-colors"
                style={{ ...pillButton(prompt.trim() === r.room), minHeight: 48, fontSize: 13.5, fontWeight: 600 }}
              >
                {r.label}
              </button>
            ))}
          </div>
          <div className="mono-label" style={{ marginBottom: 6 }}>Or describe your own room</div>
          <p style={{ fontSize: 12.5, color: 'var(--hj-ink-2)', margin: '0 0 10px', lineHeight: 1.5 }}>
            Keep it simple: one sentence with the place and the light. "A brownstone sidewalk on a sunny afternoon" works. Long descriptions give the AI more to redraw, including you.
          </p>
        </>
      ) : (
        <>
          <div className="mono-label" style={{ marginBottom: 8 }}>Pick a look</div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" style={{ marginBottom: 18 }}>
        {LOOKS.map(l => {
          const active = prompt.trim() === l.prompt;
          return (
            <button
              key={l.key}
              type="button"
              onClick={() => { setPrompt(l.prompt); setImproved(null); setSelectedSetId(null); }}
              className="text-left overflow-hidden transition-colors"
              style={{ ...pillButton(active), padding: 0 }}
            >
              <img
                src={`/scene-edit-looks/${l.key}.jpg?v=3`}
                alt=""
                loading="lazy"
                className="w-full block"
                style={{ aspectRatio: '16 / 9', objectFit: 'cover', background: 'var(--hj-surface-3)' }}
              />
              <div style={{ padding: '8px 10px', fontSize: 13, fontWeight: 600 }}>{l.label}</div>
            </button>
          );
        })}
      </div>

      <div className="mono-label" style={{ marginBottom: 8 }}>Or describe your own</div>
      <ul style={{ fontSize: 12.5, color: 'var(--hj-ink-2)', margin: '0 0 10px', paddingLeft: 18, lineHeight: 1.5 }}>
        {PROMPT_TIPS.map(t => <li key={t}>{t}</li>)}
      </ul>
        </>
      )}
      <textarea
        value={prompt}
        onChange={(e) => { setPrompt(e.target.value); if (improved) setImproved(null); }}
        placeholder={roomMode ? 'a quiet bookstore at night, a green desk lamp at camera-left, shelves behind me, rain on the window at camera-right…' : 'Relight the scene with warm golden-hour light…'}
        rows={3}
        style={{
          width: '100%', padding: 12, borderRadius: 'var(--hj-r-sm)', border: '1px solid var(--hj-hairline)',
          background: 'var(--hj-surface-2)', color: 'var(--hj-ink)', fontSize: 14, fontFamily: 'inherit', resize: 'vertical',
        }}
      />
      {driftWord && keepPeople && (
        <p role="note" style={{ margin: '8px 0 0', padding: '8px 12px', borderRadius: 'var(--hj-r-sm)', background: 'var(--hj-yellow-tint)', border: '1px solid var(--hj-yellow)', fontSize: 13, lineHeight: 1.45, color: 'var(--hj-ink)' }}>
          "{driftWord}" asks the edit to change the person or what they're holding, and that's what makes faces change. Describe the place and the light instead.
        </p>
      )}
      <LightConflictNote prompt={prompt} side={lightSide} onSwap={() => { setPrompt(p => swapLeftRight(p)); setImproved(null); }} />
      <div className="flex items-center gap-2 flex-wrap" style={{ marginTop: 8 }}>
        <button
          type="button"
          onClick={improvePrompt}
          disabled={!prompt.trim() || improving}
          className="inline-flex items-center gap-1.5"
          style={{
            padding: '6px 12px', borderRadius: 999, border: '1px solid var(--hj-blue)', background: 'var(--hj-blue-tint)',
            color: 'var(--hj-ink)', fontSize: 12.5, fontWeight: 600, fontFamily: 'inherit',
            cursor: !prompt.trim() || improving ? 'not-allowed' : 'pointer', opacity: !prompt.trim() || improving ? 0.5 : 1,
          }}
        >
          {improving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
          {improving ? 'Improving…' : 'Improve my prompt'}
        </button>
        {improved && (
          <button
            type="button"
            onClick={() => { setPrompt(improved.from); setImproved(null); }}
            style={{ padding: '6px 10px', borderRadius: 999, border: 'none', background: 'transparent', color: 'var(--hj-ink-2)', fontSize: 12.5, fontFamily: 'inherit', cursor: 'pointer', textDecoration: 'underline' }}
          >
            Undo
          </button>
        )}
      </div>
      {improved?.note && (
        <p style={{ fontSize: 12.5, color: 'var(--hj-ink-2)', margin: '6px 0 0', lineHeight: 1.45 }}>{improved.note}</p>
      )}

      {roomMode && (
        <div style={{ marginTop: 14 }}>
          <div className="mono-label" style={{ marginBottom: 6 }}>What you're wearing</div>
          <input
            value={wearing}
            onChange={(e) => setWearing(e.target.value.slice(0, 100))}
            placeholder={wearingStatus === 'reading' ? 'Reading it from your clip…' : 'black t-shirt, silver chain'}
            aria-label="What you're wearing"
            style={{
              width: '100%', minHeight: 44, padding: '0 12px', borderRadius: 'var(--hj-r-sm)', border: '1px solid var(--hj-hairline)',
              background: 'var(--hj-surface-2)', color: 'var(--hj-ink)', fontSize: 16, fontFamily: 'inherit', boxSizing: 'border-box',
            }}
          />
          <p style={{ fontSize: 12.5, color: 'var(--hj-ink-2)', margin: '6px 0 0', lineHeight: 1.45 }}>
            Naming your clothes keeps them exactly as filmed. Fix it if we got it wrong.
          </p>
        </div>
      )}

      {!(showStill || longMode || !!guide || refPhotos.length > 0) && (
        <button type="button" onClick={() => setShowStill(true)}
          style={{ marginTop: 18, background: 'none', border: 0, padding: 0, minHeight: 32, color: 'var(--hj-ink-2)', textDecoration: 'underline', textUnderlineOffset: 3, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13 }}>
          Optional: match a still or a photo of a real place
        </button>
      )}
      {(showStill || longMode || !!guide || refPhotos.length > 0) && <>
      {/* A photo of a real place is the backdrop: it feeds the guide frame, which the edit then matches */}
      <div style={{ marginTop: 18, padding: '14px 16px', borderRadius: 'var(--hj-r-md)', border: '1px solid var(--hj-hairline)', background: 'var(--hj-surface-1)' }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--hj-ink)' }}>Or use a photo of the place</div>
        <div style={{ fontSize: 12.5, color: 'var(--hj-ink-2)', marginTop: 2, lineHeight: 1.45 }}>
          Add up to 2 photos of a real location. We'll make a guide frame of your shot in that place (1 video token), and the edit matches it.
        </div>
        <div className="flex items-center gap-2 flex-wrap" style={{ marginTop: 10 }}>
          {refPhotos.map((r, i) => (
            <div key={r.key} className="relative" style={{ width: 64, height: 64 }}>
              <img src={r.url} alt="" className="w-full h-full" style={{ objectFit: 'cover', borderRadius: 'var(--hj-r-sm)', border: '1px solid var(--hj-hairline)' }} />
              <button
                type="button"
                onClick={() => setRefPhotos(prev => prev.filter((_, j) => j !== i))}
                aria-label="Remove photo"
                style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: 999, border: '1px solid var(--hj-hairline)', background: 'var(--hj-surface-1)', color: 'var(--hj-ink-2)', fontSize: 12, lineHeight: '18px', cursor: 'pointer', padding: 0 }}
              >×</button>
            </div>
          ))}
          {refPhotos.length < 2 && (
            <label
              className="inline-flex items-center gap-1.5"
              style={{ padding: '7px 12px', borderRadius: 999, border: '1px solid var(--hj-hairline)', color: 'var(--hj-ink-2)', fontSize: 12.5, fontWeight: 600, cursor: refUploading ? 'wait' : 'pointer' }}
            >
              {refUploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              Add a location photo
              <input type="file" accept="image/*" hidden disabled={refUploading} onChange={(e) => { const f = e.target.files?.[0]; if (f) addRefPhoto(f); e.target.value = ''; }} />
            </label>
          )}
          {refPhotos.length > 0 && (
            <button
              type="button"
              onClick={() => { if (!prompt.trim()) setPrompt(roomMode ? 'the place in the photo, with its own lamps and windows' : 'Replace the background with the location in the photo.'); setTimeout(() => guideBtnRef.current?.click(), 60); }}
              disabled={guide?.status === 'making'}
              className="inline-flex items-center gap-1.5"
              style={{ minHeight: 40, padding: '8px 14px', borderRadius: 999, border: 0, background: 'var(--hj-yellow)', color: 'oklch(0.18 0.005 70)', fontSize: 13, fontWeight: 700, fontFamily: 'inherit', cursor: guide?.status === 'making' ? 'wait' : 'pointer' }}
            >
              {guide?.status === 'making' ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Making guide frame…</> : <><Wand2 className="w-3.5 h-3.5" /> Make guide frame · 1 video token</>}
            </button>
          )}
        </div>
      </div>

      </>}

      {/* Room mode always locks the person (face, hair, clothes, performance) */}
      {!roomMode && <label
        className="flex items-start gap-3 cursor-pointer"
        style={{ marginTop: 18, padding: 12, borderRadius: 'var(--hj-r-sm)', background: 'var(--hj-surface-2)', border: '1px solid var(--hj-hairline-soft)' }}
      >
        <input
          type="checkbox"
          checked={keepPeople}
          onChange={(e) => setKeepPeople(e.target.checked)}
          style={{ marginTop: 3, width: 16, height: 16, accentColor: 'var(--hj-blue)' }}
        />
        <span>
          <span style={{ display: 'block', fontSize: 13.5, fontWeight: 600, color: 'var(--hj-ink)' }}>Keep people real</span>
          <span style={{ display: 'block', fontSize: 12, color: 'var(--hj-ink-2)', marginTop: 2, lineHeight: 1.45 }}>
            Leaves faces, hair, clothing and movement exactly as filmed. Turn off only if you want the people restyled too.
          </span>
        </span>
      </label>}

      {(showStill || longMode || !!guide || refPhotos.length > 0) && <div style={{ marginTop: 18, paddingTop: 16, borderTop: '1px solid var(--hj-hairline-soft)' }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--hj-ink)' }}>Optional: see a still · {selectedSet ? 'free with your saved set' : '1 video token'}</div>
        <div style={{ fontSize: 12, color: 'var(--hj-ink-2)', marginTop: 2, lineHeight: 1.45 }}>
          A still of your shot at {fmt(start)} in the new setting. If you use it, the edit matches it. It can look pasted on, so most edits look better without one.
          {refPhotos.length > 0 && ` Uses your ${refPhotos.length === 1 ? 'photo' : 'photos'} of the place.`}
        </div>
        <div className="flex items-center gap-2 flex-wrap" style={{ marginTop: 10 }}>
          <button
            ref={guideBtnRef}
            type="button"
            onClick={handleMakeGuide}
            disabled={!prompt.trim() || guide?.status === 'making'}
            className="inline-flex items-center gap-1.5"
            style={{
              padding: '8px 14px', borderRadius: 999, border: '1px solid var(--hj-hairline)', background: 'transparent',
              color: 'var(--hj-ink)', fontSize: 12.5, fontWeight: 600, fontFamily: 'inherit',
              cursor: !prompt.trim() || guide?.status === 'making' ? 'not-allowed' : 'pointer',
              opacity: !prompt.trim() || guide?.status === 'making' ? 0.5 : 1,
            }}
          >
            {guide?.status === 'making'
              ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Making guide frame…</>
              : selectedSet
                ? <><Wand2 className="w-3.5 h-3.5" /> Put this shot in {selectedSet.name} · free</>
                : <><Wand2 className="w-3.5 h-3.5" /> {guide?.status === 'ready' ? 'Make another' : 'Make guide frame'} · 1 video token</>}
          </button>
        </div>
        {guide?.status === 'failed' && (
          <p style={{ fontSize: 12.5, color: 'hsl(var(--destructive))', margin: '10px 0 0' }}>{guide.error}</p>
        )}
        {guide?.status === 'ready' && guide.url && (
          <div style={{ marginTop: 12 }}>
            <img src={guide.url} alt="Guide frame" className="w-full block" style={{ borderRadius: 'var(--hj-r-md)', border: '1px solid var(--hj-hairline)' }} />
            {guideMatches ? (
              <>
                <label className="flex items-center gap-2 cursor-pointer" style={{ marginTop: 8, fontSize: 13, color: 'var(--hj-ink)' }}>
                  <input type="checkbox" checked={useGuide} onChange={(e) => setUseGuide(e.target.checked)} style={{ width: 16, height: 16, accentColor: 'var(--hj-blue)' }} />
                  Match this frame
                </label>
                {!guide.fromSet && guide.key && (
                  <SaveSetForm key={guide.key} saved={savedGuideKeys.includes(guide.key)} onSave={name => saveSet(guide.key!, name)} />
                )}
              </>
            ) : (
              <div className="mono-label" style={{ marginTop: 8 }}>Made for {fmt(guide.start)}. Your start point changed, so make a new one to use it.</div>
            )}
          </div>
        )}
      </div>}

      <div style={{ marginTop: 18, paddingTop: 16, borderTop: '1px solid var(--hj-hairline-soft)' }}>
        {shortRender && (
          <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.45, color: 'var(--hj-ink)' }}>
            Short clips skip the preview and render straight away: {shortLen} seconds for {tier.credits} video tokens.
          </p>
        )}
        {!shortRender && <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--hj-ink)' }}>1. Preview {PREVIEW.seconds} seconds · {PREVIEW.credits} video tokens</div>
            <div style={{ fontSize: 12, color: 'var(--hj-ink-2)', marginTop: 2 }}>
              Preview {PREVIEW.seconds} seconds from {fmt(start)} for {PREVIEW.credits} video tokens before the full edit.
            </div>
          </div>
          <button
            type="button"
            onClick={handlePreview}
            disabled={!prompt.trim() || preview?.status === 'running'}
            className="inline-flex items-center gap-1.5"
            style={{
              padding: '10px 16px', minHeight: 44, borderRadius: 999,
              border: previewFresh ? '1px solid var(--hj-hairline)' : 0,
              background: previewFresh ? 'transparent' : 'var(--hj-yellow)',
              color: previewFresh ? 'var(--hj-ink)' : 'oklch(0.18 0.005 70)', fontSize: 13.5, fontWeight: 700, fontFamily: 'inherit',
              cursor: !prompt.trim() || preview?.status === 'running' ? 'not-allowed' : 'pointer',
              opacity: !prompt.trim() || preview?.status === 'running' ? 0.5 : 1,
            }}
          >
            {preview?.status === 'running'
              ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Previewing · {fmt(previewElapsed)}</>
              : <><Wand2 className="w-3.5 h-3.5" /> Preview {PREVIEW.seconds} seconds · {PREVIEW.credits} video tokens</>}
          </button>
        </div>}
        {preview?.status === 'running' && (
          <p style={{ fontSize: 12, color: 'var(--hj-ink-2)', margin: '10px 0 0' }}>
            Previews usually take a few minutes. You can keep adjusting while it runs.
          </p>
        )}
        {preview?.status === 'failed' && (
          <p style={{ fontSize: 12.5, color: 'hsl(var(--destructive))', margin: '10px 0 0' }}>{preview.error}</p>
        )}
        {preview?.status === 'done' && preview.url && (
          <div ref={previewCardRef} style={{ marginTop: 12, scrollMarginTop: 90 }}>
            {previewIsStale && (
              <p style={{ margin: '0 0 8px', fontSize: 13, lineHeight: 1.45, color: 'var(--hj-ink-2)' }}>
                This preview was made from a different description or settings. Preview the new one, or{' '}
                <button
                  type="button"
                  onClick={restorePreviewed}
                  style={{ background: 'none', border: 0, padding: 0, color: 'var(--hj-ink)', textDecoration: 'underline', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 600 }}
                >
                  go back to what this preview used
                </button>.
              </p>
            )}
            <div className="aspect-video overflow-hidden" style={{ background: 'oklch(0.08 0.005 70)', borderRadius: 'var(--hj-r-md)', border: '1px solid var(--hj-hairline)' }}>
              <video src={preview.url} className="w-full h-full object-contain" controls autoPlay muted loop playsInline />
            </div>
            {previewFresh && preview?.likeness && <LikenessLine likeness={preview.likeness} preview />}
            {previewDrifted && !longMode && (
              <div style={{ marginTop: 10 }}>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={handlePreview}
                    style={{ minHeight: 44, padding: '10px 16px', borderRadius: 999, border: 0, background: 'var(--hj-yellow)', color: 'oklch(0.18 0.005 70)', fontSize: 13.5, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer' }}
                  >
                    Preview again · {PREVIEW.credits} video tokens
                  </button>
                </div>
                <p style={{ margin: '8px 0 0', fontSize: 12.5, lineHeight: 1.45, color: 'var(--hj-ink-2)' }}>
                  <button
                    type="button"
                    onClick={handleStartEdit}
                    style={{ background: 'none', border: 0, padding: 0, color: 'var(--hj-ink)', textDecoration: 'underline', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 600 }}
                  >
                    Render the full {tier.seconds} seconds anyway
                  </button>{' '}({tier.credits} video tokens; no free redo when the preview already showed the faces changing).
                </p>
              </div>
            )}
            {previewFresh && !previewDrifted && !longMode && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--hj-ink)', marginBottom: 2 }}>Happy with it?</div>
                <p style={{ margin: '0 0 10px', fontSize: 13, lineHeight: 1.45, color: 'var(--hj-ink-2)' }}>This was the first 5 seconds. The next step renders your whole {tier.seconds}-second clip the same way.</p>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={handleStartEdit}
                    style={{ minHeight: 44, padding: '10px 16px', borderRadius: 999, border: 0, background: 'var(--hj-yellow)', color: 'oklch(0.18 0.005 70)', fontSize: 13.5, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer' }}
                  >
                    Yes, render the full {tier.seconds} seconds · {tier.credits} video tokens
                  </button>
                  <button
                    type="button"
                    onClick={handlePreview}
                    style={{ minHeight: 44, padding: '10px 16px', borderRadius: 999, border: '1px solid var(--hj-hairline)', background: 'transparent', color: 'var(--hj-ink)', fontSize: 13.5, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer' }}
                  >
                    Retry same preview · {PREVIEW.credits} video tokens
                  </button>
                </div>
                <p style={{ margin: '8px 0 0', fontSize: 13, lineHeight: 1.45, color: 'var(--hj-ink-2)' }}>
                  Or change one thing and preview again: place first, then light, then weather.
                </p>
              </div>
            )}
          </div>
        )}
        {!previewFresh && !longMode && !shortRender && prompt.trim() && preview?.status !== 'running' && (
          <p style={{ margin: '10px 0 0', fontSize: 12.5, lineHeight: 1.45, color: 'var(--hj-ink-2)' }}>
            2. The full {tier.seconds} seconds costs {tier.credits} video tokens. When a preview of the same edit passed and the full render still changes the faces, you get those tokens back once. Skipping the preview gives that up.{' '}
            <button
              type="button"
              onClick={handleStartEdit}
              disabled={duration > 0 && end - start < 2}
              style={{ background: 'none', border: 0, padding: 0, color: 'var(--hj-ink)', textDecoration: 'underline', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 600 }}
            >
              Skip the preview and render now
            </button>
          </p>
        )}
      </div>
      {video?.s3Key && video.status === 'uploaded' && <SceneAnglesPanel videoKey={video.s3Key} videoUrl={video.s3Url} startTime={start} wearing={wearing} label="this clip" />}
    </StepCard>
  );

  const renderProcessStep = () => (
    <StepCard>
      <StepHeader kicker="Step 03 · Process" title="Editing your scene." subtitle={prompt ? `"${prompt}"` : undefined} />

      {(processStatus === 'starting' || processStatus === 'processing') && (
        <div style={{ padding: 16, borderRadius: 'var(--hj-r-sm)', background: 'var(--hj-surface-2)', border: '1px solid var(--hj-hairline-soft)' }}>
          <div className="flex items-center gap-3">
            <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--hj-yellow-ink)' }} />
            <span style={{ fontSize: 13.5, color: 'var(--hj-ink)' }}>
              {processStatus === 'starting' ? 'Preparing your clip…'
                : longProgress?.stitching ? `Joining the pieces with your original audio · ${fmt(elapsed)} elapsed`
                : longProgress ? `Editing ${longProgress.total} pieces, ${longProgress.done} done · ${fmt(elapsed)} elapsed`
                : `Editing · ${fmt(elapsed)} elapsed`}
            </span>
          </div>
          <p style={{ marginTop: 12, marginBottom: 0, fontSize: 12.5, color: 'var(--hj-ink-2)', lineHeight: 1.5 }}>
            Scene edits usually take 10 to 15 minutes. You can close this tab: the finished clip is saved to My Library either way.
          </p>
          {processStatus === 'processing' && (
            <button
              type="button"
              onClick={resetAll}
              className="inline-flex items-center gap-1.5"
              style={{
                marginTop: 12, padding: '7px 14px', borderRadius: 999, border: '1px solid var(--hj-hairline)',
                background: 'transparent', color: 'var(--hj-ink-2)', fontSize: 12.5, fontFamily: 'inherit', cursor: 'pointer',
              }}
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Start a new edit
            </button>
          )}
        </div>
      )}

      {processStatus === 'failed' && (
        <div style={{ padding: 16, borderRadius: 'var(--hj-r-sm)', border: '1px solid hsl(var(--destructive) / 0.5)', background: 'hsl(var(--destructive) / 0.08)' }}>
          <div className="flex items-center gap-2" style={{ marginBottom: 6 }}>
            <AlertCircle className="w-5 h-5" style={{ color: 'hsl(var(--destructive))' }} />
            <p style={{ fontWeight: 600, color: 'hsl(var(--destructive))', margin: 0 }}>Scene edit failed</p>
          </div>
          {processError && <p style={{ fontSize: 13, color: 'hsl(var(--destructive) / 0.85)', margin: 0 }}>{processError}</p>}
        </div>
      )}

      {processStatus === 'completed' && (
        <div
          className="flex items-center gap-3"
          style={{ padding: '14px 16px', borderRadius: 'var(--hj-r-sm)', background: 'oklch(0.42 0.15 145 / 0.12)', border: '1px solid var(--hj-green)', color: 'var(--hj-ink)' }}
        >
          <CheckCircle className="w-5 h-5" style={{ color: 'var(--hj-green)' }} />
          <div style={{ fontWeight: 600 }}>Your scene is ready</div>
          <button
            type="button"
            onClick={() => markDoneAndGo('process', 'download')}
            style={{
              marginLeft: 'auto', padding: '8px 16px', borderRadius: 999, background: 'var(--hj-blue)', border: '1px solid var(--hj-blue)',
              color: 'oklch(0.18 0.005 70)', fontSize: 13, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer',
            }}
          >
            View and download
          </button>
        </div>
      )}
    </StepCard>
  );

  const renderDownloadStep = () => (
    <StepCard>
      <StepHeader kicker="Step 04 · Download" title="Here's your new scene." />
      {finalLikeness && <LikenessLine likeness={finalLikeness} />}
      {renderGuideKey && !(guide?.fromSet && guide.key === renderGuideKey) && (
        <div style={{ marginBottom: 16 }}>
          <SaveSetForm key={renderGuideKey} saved={savedGuideKeys.includes(renderGuideKey)} onSave={name => saveSet(renderGuideKey, name)} />
        </div>
      )}
      {canJoin && (
        <div style={{ marginBottom: 16, padding: '14px 16px', borderRadius: 'var(--hj-r-md)', background: 'var(--hj-surface-2)', border: '1px solid var(--hj-hairline-soft)' }}>
          <div style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--hj-ink)' }}>
            Part {sceneChain!.parts.length + 1} is done. Join it with the {sceneChain!.parts.length === 1 ? 'first part' : `first ${sceneChain!.parts.length} parts`}?
          </div>
          <p style={{ margin: '4px 0 10px', fontSize: 13, lineHeight: 1.45, color: 'var(--hj-ink-2)' }}>
            One clip, with your original audio. Free, and it saves to My Library.
          </p>
          <button
            type="button"
            onClick={joinParts}
            disabled={joinState?.status === 'joining'}
            className="inline-flex items-center gap-2"
            style={{ minHeight: 44, padding: '10px 18px', borderRadius: 999, border: 0, background: 'var(--hj-yellow)', color: 'oklch(0.18 0.005 70)', fontSize: 14, fontWeight: 700, fontFamily: 'inherit', cursor: joinState?.status === 'joining' ? 'wait' : 'pointer' }}
          >
            {joinState?.status === 'joining' ? <><Loader2 className="w-4 h-4 animate-spin" /> Joining…</> : 'Join the parts'}
          </button>
          {joinState?.status === 'failed' && <p style={{ margin: '8px 0 0', fontSize: 12.5, color: 'hsl(var(--destructive))' }}>{joinState.error}</p>}
        </div>
      )}
      <button
        type="button"
        onClick={() => navigate('/my-library')}
        className="inline-flex items-center gap-1.5"
        style={{ margin: '-10px 0 16px', minHeight: 44, padding: 0, background: 'transparent', border: 0, color: 'var(--hj-ink)', fontSize: 14, fontWeight: 600, fontFamily: 'inherit', textDecoration: 'underline', textUnderlineOffset: 3, cursor: 'pointer' }}
      >
        <FolderOpen className="w-4 h-4" />
        Saved to My Library. Open it
      </button>
      <div className="aspect-video overflow-hidden" style={{ background: 'oklch(0.08 0.005 70)', borderRadius: 'var(--hj-r-md)', border: '1px solid var(--hj-hairline)', marginBottom: 16 }}>
        <video src={finalVideoUrl} className="w-full h-full object-contain" controls preload="auto" playsInline />
      </div>
      <div className="flex flex-col sm:flex-row gap-2.5">
        <button
          type="button"
          onClick={handleDownload}
          className="inline-flex items-center justify-center gap-2"
          style={{
            padding: '12px 22px', borderRadius: 999, background: 'var(--hj-blue)', border: '1px solid var(--hj-blue)',
            color: 'oklch(0.18 0.005 70)', fontSize: 15, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', minHeight: 48,
          }}
        >
          <Download className="w-4 h-4" />
          Save to device
        </button>
        <button
          type="button"
          onClick={keepEditingResult}
          className="inline-flex items-center justify-center gap-2"
          style={{
            padding: '12px 22px', borderRadius: 999, background: 'transparent', border: '1px solid var(--hj-hairline)',
            color: 'var(--hj-ink-2)', fontSize: 14, fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer', minHeight: 48,
          }}
        >
          <Wand2 className="w-4 h-4" />
          Keep editing this result
        </button>
        {renderedLine && (
          <button
            type="button"
            onClick={swapAnotherLine}
            disabled={!currentResultKey}
            className="inline-flex items-center justify-center gap-2"
            style={{
              padding: '12px 22px', borderRadius: 999, background: 'transparent', border: '1px solid var(--hj-hairline)',
              color: 'var(--hj-ink)', fontSize: 14, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', minHeight: 48,
            }}
          >
            <ArrowRight className="w-4 h-4" />
            Swap another line
          </button>
        )}
        <button
          type="button"
          onClick={() => continueScene(currentResultKey)}
          disabled={continuing || !currentResultKey}
          className="inline-flex items-center justify-center gap-2"
          style={{
            padding: '12px 22px', borderRadius: 999, background: 'transparent', border: '1px solid var(--hj-hairline)',
            color: 'var(--hj-ink)', fontSize: 14, fontWeight: 600, fontFamily: 'inherit', cursor: continuing ? 'wait' : 'pointer', minHeight: 48,
          }}
        >
          {continuing ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
          Continue this scene
        </button>
        <button
          type="button"
          onClick={resetAll}
          className="inline-flex items-center justify-center gap-2"
          style={{
            padding: '12px 22px', borderRadius: 999, background: 'transparent', border: '1px solid var(--hj-hairline)',
            color: 'var(--hj-ink-2)', fontSize: 14, fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer', minHeight: 48,
          }}
        >
          <RotateCcw className="w-4 h-4" />
          Start over
        </button>
      </div>

      {sceneLibrary.length > 0 && (
        <div style={{ marginTop: 24, paddingTop: 18, borderTop: '1px solid var(--hj-hairline-soft)' }}>
          <div className="flex items-center justify-between gap-3" style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--hj-ink)' }}>Your scene edits</div>
            <button
              type="button"
              onClick={() => navigate('/my-library')}
              style={{ minHeight: 44, padding: '0 4px', background: 'transparent', border: 0, color: 'var(--hj-ink)', fontSize: 13.5, fontWeight: 600, fontFamily: 'inherit', textDecoration: 'underline', textUnderlineOffset: 3, cursor: 'pointer' }}
            >
              See all in My Library
            </button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {sceneLibrary.map(item => {
              const url = `/api/s3/${item.s3Key}`;
              const playing = finalVideoUrl === url;
              return (
                <div key={item.id} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <button
                    type="button"
                    onClick={() => { setFinalVideoUrl(url); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                    aria-label={`Play ${item.name}`}
                    className="aspect-video overflow-hidden"
                    style={{ padding: 0, borderRadius: 'var(--hj-r-sm)', border: playing ? '2px solid var(--hj-yellow)' : '1px solid var(--hj-hairline)', background: 'oklch(0.08 0.005 70)', cursor: 'pointer' }}
                  >
                    <video src={`${url}#t=0.5`} className="w-full h-full object-cover" preload="metadata" muted playsInline style={{ pointerEvents: 'none' }} />
                  </button>
                  <div style={{ fontSize: 12.5, lineHeight: 1.35, color: 'var(--hj-ink)', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                    {item.name.replace(/^Scene edit( \(\d+s\))? - /, '')}
                  </div>
                  <a
                    href={url}
                    download={`scene-edit-${item.id}.mp4`}
                    className="inline-flex items-center gap-1"
                    style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--hj-ink)', minHeight: 32 }}
                  >
                    <Download className="w-3.5 h-3.5" /> Save
                  </a>
                  {/^Scene edit/.test(item.name) && (
                    <button
                      type="button"
                      onClick={() => continueScene(item.s3Key)}
                      disabled={continuing}
                      className="inline-flex items-center gap-1 text-left"
                      style={{ padding: 0, minHeight: 32, background: 'transparent', border: 0, fontSize: 12.5, fontWeight: 600, color: 'var(--hj-ink)', fontFamily: 'inherit', cursor: 'pointer' }}
                    >
                      <ArrowRight className="w-3.5 h-3.5" /> Continue
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      {currentResultKey && <SceneFinishPanel resultKey={currentResultKey} prompt={prompt} authHeaders={getAuthHeaders} />}
      {currentResultKey && <SceneAnglesPanel videoKey={currentResultKey} videoUrl={finalVideoUrl} wearing={wearing} label="your edit" />}
    </StepCard>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 'upload': return renderUploadStep();
      case 'describe': return renderDescribeStep();
      case 'process': return renderProcessStep();
      case 'download': return renderDownloadStep();
    }
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--hj-bg)' }}>
      <ToolShell
        toolName="Scene Edit"
        toolNumber={`Tool · ${String(stepIndex + 1).padStart(2, '0')} / ${String(STEPS.length).padStart(2, '0')}`}
        usesCredits
        steps={STEPS as ToolStep[]}
        currentStepKey={currentStep}
        completedKeys={completedSteps as Set<string>}
        onStepChange={(k) => goToStep(k as WizardStep)}
        onBack={() => { if (stepIndex > 0) goToStep(STEP_ORDER[stepIndex - 1]); }}
        onAdvance={advance.onAdvance}
        advanceLabel={advance.label}
        advanceDisabled={advance.disabled}
        showAdvance={advance.showAdvance !== false}
        onBackToStudio={() => navigate('/')}
      >
        <SceneEditVideoIntro />
        <SceneEditIntro />
        {renderCurrentStep()}
        {/* The toolbar's Continue sits at the top and is easy to miss once you've
            scrolled through a step, so every step repeats it at the bottom (process
            only once the edit is done or failed, not the disabled "Editing…"). */}
        {advance.showAdvance !== false && !(currentStep === 'process' && advance.disabled) && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '20px 0 8px' }}>
            <button
              type="button"
              onClick={advance.onAdvance}
              disabled={!!advance.disabled}
              style={{ width: '100%', maxWidth: 420, padding: '15px 22px', borderRadius: 999, border: 0, background: 'var(--hj-yellow)', color: 'oklch(0.18 0.005 70)', fontSize: 16, fontWeight: 800, fontFamily: 'inherit', cursor: advance.disabled ? 'not-allowed' : 'pointer', opacity: advance.disabled ? 0.45 : 1 }}
            >
              {advance.label || 'Continue'} →
            </button>
          </div>
        )}
      </ToolShell>

      <CreditConfirmDialog />
      <UpgradeModal open={showUpgradeModal} onOpenChange={setShowUpgradeModal} trigger="ai_feature" />
      <LoginModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} />
      <LibraryPicker open={showLibraryPicker} onClose={() => setShowLibraryPicker(false)} onPick={handleLibraryPick} />
    </div>
  );
};// One line saying whether the person kept their face, under a preview or a result.
const LikenessLine: React.FC<{ likeness: Likeness; preview?: boolean }> = ({ likeness, preview = false }) => {
  if (likeness.status === 'error') return null;
  const tone = likeness.status === 'drift'
    ? { bg: 'var(--hj-yellow-tint)', line: 'var(--hj-yellow)' }
    : { bg: 'var(--hj-surface-2)', line: 'var(--hj-hairline-soft)' };
  let text: string;
  if (likeness.status === 'ok') text = preview ? 'Faces held. We checked the people against your original, so this is safe to render.' : 'Faces checked against your original: they held.';
  else if (likeness.status === 'no_faces') text = 'No clear face to check in this part, so look closely before you render.';
  else if (preview) text = 'The faces changed in this preview. The full render would most likely do the same. Describe only the place and the light, or add a guide frame, then preview again.';
  else if (likeness.refunded) text = 'The faces changed in this render, so your video tokens are back. Try describing only the place and the light, or add a guide frame.';
  else if (likeness.refundNote === 'redo_used') text = 'The faces changed in this render. Your free redo for this clip is already used. Describe only the place and the light before you try again.';
  else text = 'The faces changed in this render. Preview first next time: when a passing preview is followed by a full render that changes faces, you get the tokens back once.';
  return (
    <p role="status" style={{ margin: '10px 0 12px', padding: '10px 12px', borderRadius: 'var(--hj-r-sm)', background: tone.bg, border: `1px solid ${tone.line}`, fontSize: 13.5, lineHeight: 1.5, color: 'var(--hj-ink)' }}>
      {text}
    </p>
  );
};

// Finish it: the sound of the place, and versions shaped for Instagram (server
// sceneFinish.js). Both free; both save to My Library. Formats are cropped around the
// actor, never padded or blur-filled, so only shapes narrower than the clip appear.
const SceneFinishPanel: React.FC<{ resultKey: string; prompt: string; authHeaders: () => Record<string, string> }> = ({ resultKey, prompt, authHeaders }) => {
  const [level, setLevel] = useState<'light' | 'more'>('light');
  const [tone, setTone] = useState<{ status: 'idle' | 'running' | 'done' | 'failed'; url?: string; key?: string; error?: string }>({ status: 'idle' });
  const [formats, setFormats] = useState<{ id: string; label: string }[] | null>(null);
  const [made, setMade] = useState<Record<string, { status: 'running' | 'done' | 'failed'; url?: string; error?: string }>>({});
  const base = tone.status === 'done' && tone.key ? tone.key : resultKey;

  useEffect(() => { setTone({ status: 'idle' }); setMade({}); }, [resultKey]);
  useEffect(() => {
    let live = true;
    fetch(`/api/scene-edit/formats?s3Key=${encodeURIComponent(resultKey)}`, { headers: authHeaders() })
      .then(r => (r.ok ? r.json() : null)).then(d => { if (live) setFormats(d?.formats || []); }).catch(() => { if (live) setFormats([]); });
    return () => { live = false; };
  }, [resultKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const addTone = async () => {
    setTone({ status: 'running' });
    try {
      const r = await fetch('/api/scene-edit/room-tone', { method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({ s3Key: resultKey, prompt, level }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Could not start the sound');
      const started = Date.now();
      const poll = async () => {
        const st = await fetch(`/api/scene-edit/room-tone/${d.id}`, { headers: authHeaders() }).then(x => x.json()).catch(() => ({ status: 'processing' }));
        if (st.status === 'completed') { setTone({ status: 'done', url: st.url, key: st.s3Key }); setMade({}); return; }
        if (st.status === 'failed') { setTone({ status: 'failed', error: st.error }); return; }
        if (Date.now() - started > 5 * 60000) { setTone({ status: 'failed', error: 'The sound is taking too long. Try again.' }); return; }
        setTimeout(poll, 4000);
      };
      setTimeout(poll, 4000);
    } catch (e: any) { setTone({ status: 'failed', error: e.message }); }
  };

  const makeFormat = async (id: string) => {
    setMade(m => ({ ...m, [id]: { status: 'running' } }));
    try {
      const r = await fetch('/api/scene-edit/formats', { method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({ s3Key: base, format: id }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Could not make that version');
      setMade(m => ({ ...m, [id]: { status: 'done', url: d.url } }));
    } catch (e: any) { setMade(m => ({ ...m, [id]: { status: 'failed', error: e.message } })); }
  };

  const card: React.CSSProperties = { marginTop: 16, padding: '14px 16px', borderRadius: 'var(--hj-r-md)', background: 'var(--hj-surface-2)', border: '1px solid var(--hj-hairline-soft)' };
  const pill = (active = false): React.CSSProperties => ({ minHeight: 40, padding: '8px 14px', borderRadius: 999, border: active ? 0 : '1px solid var(--hj-hairline)', background: active ? 'var(--hj-yellow)' : 'transparent', color: 'var(--hj-ink)', fontSize: 13.5, fontWeight: 650, fontFamily: 'inherit', cursor: 'pointer' });
  const dl = (url: string) => `${url}${url.includes('?') ? '&' : '?'}download=1`;

  return (
    <div>
      <div style={card}>
        <div style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--hj-ink)' }}>Add the sound of the place</div>
        <p style={{ margin: '4px 0 10px', fontSize: 13, lineHeight: 1.45, color: 'var(--hj-ink)' }}>
          A new place needs to sound like one. We add the room tone and background sound under your own audio, and it dips whenever you speak. Free.
        </p>
        {tone.status !== 'done' && (
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" style={pill(level === 'light')} onClick={() => setLevel('light')} aria-pressed={level === 'light'}>Light</button>
            <button type="button" style={pill(level === 'more')} onClick={() => setLevel('more')} aria-pressed={level === 'more'}>More</button>
            <button type="button" onClick={addTone} disabled={tone.status === 'running'} className="inline-flex items-center gap-2"
              style={{ ...pill(true), background: 'var(--hj-ink)', color: '#FFFBF0', cursor: tone.status === 'running' ? 'wait' : 'pointer' }}>
              {tone.status === 'running' ? <><Loader2 className="w-4 h-4 animate-spin" /> Adding sound…</> : 'Add room sound'}
            </button>
          </div>
        )}
        {tone.status === 'failed' && <p style={{ margin: '8px 0 0', fontSize: 12.5, color: 'hsl(var(--destructive))' }}>{tone.error}</p>}
        {tone.status === 'done' && tone.url && (
          <div>
            <div className="aspect-video overflow-hidden" style={{ background: 'oklch(0.08 0.005 70)', borderRadius: 'var(--hj-r-sm)', marginTop: 4 }}>
              <video src={tone.url} className="w-full h-full object-contain" controls playsInline />
            </div>
            <div className="flex flex-wrap items-center gap-2" style={{ marginTop: 10 }}>
              <a href={dl(tone.url)} style={{ ...pill(true), textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>Download with room sound</a>
              <button type="button" style={pill()} onClick={() => setTone({ status: 'idle' })}>Try another level</button>
            </div>
            <p style={{ margin: '8px 0 0', fontSize: 12.5, color: 'var(--hj-ink-2)' }}>Saved to My Library. The versions below use this one.</p>
          </div>
        )}
      </div>

      {formats && formats.length > 0 && (
        <div style={card}>
          <div style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--hj-ink)' }}>Make it for Instagram</div>
          <p style={{ margin: '4px 0 10px', fontSize: 13, lineHeight: 1.45, color: 'var(--hj-ink)' }}>
            Cropped around you, nothing stretched. Free, and saved to My Library.
          </p>
          <div className="flex flex-col gap-2">
            {formats.map(f => {
              const st = made[f.id];
              return (
                <div key={f.id} className="flex flex-wrap items-center gap-2">
                  {st?.status === 'done' && st.url
                    ? <a href={dl(st.url)} style={{ ...pill(true), textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>Download {f.label}</a>
                    : <button type="button" style={pill()} disabled={st?.status === 'running'} onClick={() => makeFormat(f.id)} className="inline-flex items-center gap-2">
                        {st?.status === 'running' ? <><Loader2 className="w-4 h-4 animate-spin" /> Making {f.label}…</> : `Make ${f.label}`}
                      </button>}
                  {st?.status === 'failed' && <span style={{ fontSize: 12.5, color: 'hsl(var(--destructive))' }}>{st.error}</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};


